/*!
 * Obrah - Pagina de Produto abr26 (pp26-gallery.js)
 * (c) 2026 Obrah. All rights reserved.
 * Proprietary and confidential.
 * Unauthorized copying, distribution or reverse engineering is strictly prohibited.
 *
 * Custom element <pp26-gallery>:
 *  - Aplica matrix3d nos slides perspective lendo data-corners
 *  - Escuta product:variant-change pra trocar o PNG da moldura em tempo real
 *  - Slider scroll-snap + dots sincronizados via IntersectionObserver
 *  - Reaplica o warp em resize (debounced)
 *
 * Dispatch de eventos:
 *  - pp26:ready (no element)
 *  - pp26:slide-change (no element, detail = { index })
 *  - pp26:frame-updated (no element, detail = { type, color, hasFrame })
 */
(function () {
  'use strict';

  // ========================================================================
  // HOMOGRAPHY MATH (matrix3d projetivo via 4 pontos)
  // ========================================================================
  function solveHomography(src, dst) {
    var A = [], b = [];
    for (var i = 0; i < 4; i++) {
      var sx = src[i][0], sy = src[i][1];
      var dx = dst[i][0], dy = dst[i][1];
      A.push([sx, sy, 1, 0, 0, 0, -sx * dx, -sy * dx]);
      b.push(dx);
      A.push([0, 0, 0, sx, sy, 1, -sx * dy, -sy * dy]);
      b.push(dy);
    }
    return gaussianSolve(A, b);
  }

  function gaussianSolve(A, b) {
    var n = A.length;
    for (var i = 0; i < n; i++) A[i] = A[i].slice().concat([b[i]]);
    for (var i = 0; i < n; i++) {
      var maxRow = i;
      for (var k = i + 1; k < n; k++) {
        if (Math.abs(A[k][i]) > Math.abs(A[maxRow][i])) maxRow = k;
      }
      var tmp = A[i]; A[i] = A[maxRow]; A[maxRow] = tmp;
      if (Math.abs(A[i][i]) < 1e-10) return new Array(n).fill(0);
      for (var k = i + 1; k < n; k++) {
        var factor = A[k][i] / A[i][i];
        for (var j = i; j <= n; j++) {
          A[k][j] -= factor * A[i][j];
        }
      }
    }
    var x = new Array(n);
    for (var i = n - 1; i >= 0; i--) {
      var sum = A[i][n];
      for (var j = i + 1; j < n; j++) sum -= A[i][j] * x[j];
      x[i] = sum / A[i][i];
    }
    return x;
  }

  function homographyToMatrix3d(h) {
    return [
      h[0], h[3], 0, h[6],
      h[1], h[4], 0, h[7],
      0,    0,    1, 0,
      h[2], h[5], 0, 1
    ];
  }

  // ========================================================================
  // <pp26-gallery> custom element
  // ========================================================================
  if (window.customElements && !customElements.get('pp26-gallery')) {
    customElements.define('pp26-gallery', class extends HTMLElement {
      connectedCallback() {
        this._slider = this.querySelector('[data-pp26-slider]');
        this._dots = Array.from(this.querySelectorAll('[data-pp26-dots] .pp26-gallery__dot'));
        this._titleCard = this.querySelector('[data-pp26-title-card]');
        this._slides = Array.from(this.querySelectorAll('.pp26__slide'));
        this._perspectiveStages = Array.from(this.querySelectorAll('.pp26__perspective-stage'));
        this._frames = Array.from(this.querySelectorAll('.pp26__frame'));
        this._orientation = this.dataset.orientation || 'vertical';
        this._handle = this.dataset.productHandle || '';
        this._frameLookup = this._parseFrameLookup(this.dataset.frameLookup);
        this._frameSource = this.dataset.frameSource || 'file_url';
        this._framePrefix = this.dataset.framePrefix || 'pp26';
        this._frameOffsets = this._parseFrameLookup(this.dataset.frameOffsets);
        this._blockDims = this._parseFrameLookup(this.dataset.blockDims);

        // Builder JSON override — quando disponível, sobrescreve corners + offsets
        this._builderData = null;
        if (this.dataset.builderJson) {
          try {
            this._builderData = JSON.parse(this.dataset.builderJson);
            var ori = this._orientation;
            // Sobrescreve offsets com os do builder
            if (this._builderData.frameOffsets && this._builderData.frameOffsets[ori]) {
              this._frameOffsets = this._builderData.frameOffsets[ori];
            }
          } catch (e) { console.warn('pp26: builder JSON parse error', e); }
        }

        this._applyAllWarps = this._applyAllWarps.bind(this);
        this._syncStageAspectToFrame = this._syncStageAspectToFrame.bind(this);
        this._handleVariantChange = this._handleVariantChange.bind(this);

        // Config de transição (cardápio de efeitos): separado p/ troca de MOLDURA
        // (acabamento) e troca de COR (mais soft). Editável no painel ?fxedit.
        // builderData.fx sobrescreve os defaults, se existir.
        this._fx = {
          frame: { effect: 'blur', dur: 560, ease: 'cubic-bezier(0.22, 1, 0.36, 1)', depth: 140, angle: 12, slide: 60, scaleOut: 0.9, scaleIn: 1.1, blur: 8, persp: 1200 },
          color: { effect: 'fade', dur: 380, ease: 'cubic-bezier(0.22, 1, 0.36, 1)', depth: 60, angle: 6, slide: 30, scaleOut: 0.96, scaleIn: 1.04, blur: 4, persp: 1200 }
        };
        if (this._builderData && this._builderData.fx) {
          if (this._builderData.fx.frame) Object.assign(this._fx.frame, this._builderData.fx.frame);
          if (this._builderData.fx.color) Object.assign(this._fx.color, this._builderData.fx.color);
        }

        // Offset de posição do GRUPO por ori×tipo×cor (alinhar molduras p/ não
        // "sambar"). Estrutura: framePos[ori][type][color|'*'] = {x,y} em % do stage.
        this._framePos = (this._builderData && this._builderData.framePos) || {};

        // Reaplicar warp imediatamente a cada frame de resize (sem piscar)
        var self = this;
        this._resizeRafId = null;
        this._handleResize = function () {
          if (self._resizeRafId) return; // já tem um frame agendado
          self._resizeRafId = requestAnimationFrame(function () {
            self._resizeRafId = null;
            self._syncStageAspectToFrame();
            self._applyAllWarps();
          });
        };

        // TRAVAMENTO DO WARP: ResizeObserver pega TODA mudança de caixa do stage
        // — não só window.resize, mas também a transição de aspect-ratio do
        // breakpoint (0.5s) e qualquer mudança de container. Re-warpa quadro a
        // quadro com a transição do transform DESLIGADA (classe --resizing) pra
        // a arte/textura seguirem o stage coladas e nunca vazarem pra fora da
        // moldura. Após estabilizar, religa a transição (pros cross-fades).
        this._roRaf = null;
        this._roSettle = null;
        if (window.ResizeObserver) {
          this._ro = new ResizeObserver(function () {
            self.classList.add('pp26-gallery--resizing');
            if (!self._roRaf) {
              self._roRaf = requestAnimationFrame(function () {
                self._roRaf = null;
                self._applyAllWarps();
              });
            }
            if (self._roSettle) clearTimeout(self._roSettle);
            self._roSettle = setTimeout(function () {
              self.classList.remove('pp26-gallery--resizing');
              self._applyAllWarps();
            }, 180);
          });
          this._perspectiveStages.forEach(function (st) { self._ro.observe(st); });
        }

        // Aplica offset inicial na moldura
        this._applyInitialOffset();

        // BOOT FIX: liquid pode ter setado data-corners com fallback genérico
        // (ex: '[[8,6],[92,6],[92,94],[8,94]]' quando o theme setting do tipo
        // não existe). Sobrescreve com corners corretos do builderData ou da
        // tabela hardcoded baseada no frame ativo. Sem isso o warp inicial fica
        // mapeando arte pro retângulo errado.
        var self = this;
        this._perspectiveStages.forEach(function (stage) {
          var frame = stage.querySelector('.pp26__frame:not([hidden])') ||
                      stage.querySelector('.pp26__frame');
          if (!frame) return;
          var t = frame.dataset.frameType;
          var c = frame.dataset.frameColor;
          if (!t || t === 'sem-moldura') return;
          var corners = self._defaultCorners(self._orientation, t, c);
          if (corners) stage.setAttribute('data-corners', JSON.stringify(corners));

          // BOOT: o Liquid pode renderizar o frame antigo. Re-resolvemos pro frame
          // canônico (migrados 2jun26 no Files) e trocamos se diferente.
          var _newSrc = self._resolveFrameUrl(self._orientation, t, c, frame.src);
          if (_newSrc && _newSrc !== frame.src) {
            frame.src = _newSrc;
          }
        });

        // BOOT FIX textura: o boot NÃO dispara product:variant-change, então a
        // textura de tecido (data-tex-corners + --pp26-tex-op) nunca era
        // configurada igual à troca de variante. Replica aqui o setup do
        // _updateFrame quando o frame inicial já é canvas.
        (function () {
          var afr = self._perspectiveStages[0] &&
            (self._perspectiveStages[0].querySelector('.pp26__frame:not([hidden])') ||
             self._perspectiveStages[0].querySelector('.pp26__frame'));
          var atype = afr && afr.dataset.frameType;
          if (atype === 'canvas' && self._builderData) {
            var _tcd = self._builderData.textureCorners && self._builderData.textureCorners[self._orientation] && self._builderData.textureCorners[self._orientation].canvas;
            var _top = self._builderData.texOpacity && self._builderData.texOpacity[self._orientation];
            self._perspectiveStages.forEach(function (stage) {
              if (_tcd) stage.setAttribute('data-tex-corners', JSON.stringify(_tcd));
            });
            if (_top != null) self.style.setProperty('--pp26-tex-op', _top);
          }
        })();

        // BOOT: aplica o offset de posição do grupo pro frame inicial.
        (function () {
          var afr = self._perspectiveStages[0] &&
            (self._perspectiveStages[0].querySelector('.pp26__frame:not([hidden])') ||
             self._perspectiveStages[0].querySelector('.pp26__frame'));
          if (afr && afr.dataset.frameType) self._applyFramePos(afr.dataset.frameType, afr.dataset.frameColor || 'mel');
        })();

        // Aplica warp inicial assim que as imagens da arte+frame fizerem layout.
        this._waitForArtLayout().then(function () {
          self._syncStageAspectToFrame();
          self._applyWithRetry(0);
        });

        // Fallback: aplica via setTimeout chain (garante layout em qualquer cenario)
        [100, 300, 600, 1200, 2500].forEach(function (delay) {
          setTimeout(function () {
            self._syncStageAspectToFrame();
            self._applyAllWarps();
          }, delay);
        });

        // Reaplica warp a cada frame de resize (sem piscar)
        window.addEventListener('resize', this._handleResize, { passive: true });

        // Slider dots
        this._initDots();

        // Variant change (vem do VariantSelects do product.js)
        document.addEventListener('product:variant-change', this._handleVariantChange);

        // Preload todas as molduras em background
        this._preloadFrames();

        // Dispara ready
        this.classList.add('pp26-gallery--ready');
        this.dispatchEvent(new CustomEvent('pp26:ready', { bubbles: true }));

        // Intro motion: mantém o loader (logo OBRAH + shimmer) visível por
        // ~1.5s após o window.load, igual à PDP default, e só então faz fade.
        (function (el) {
          var endIntro = function () {
            setTimeout(function () {
              el.classList.add('pp26-gallery--intro-done');
            }, 1500);
          };
          if (document.readyState === 'complete') endIntro();
          else window.addEventListener('load', endIntro, { once: true });
        })(this);

        // Environment slide config-driven (after ready)
        this._initEnvironment();

        // Calibration mode (after ready, so all frames/art are loaded)
        this._initCalibration();
        // Editor da textura (canvas) — só warp + opacidade da textura
        this._initTexEditor();
        // Tamanho por device (persistência + editor ?sizeedit)
        this._applySizeMul();
        this._initSizeEditor();
        this._initResBadge();
        this._initFxEditor();
        this._initPosEditor();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this._handleResize);
        document.removeEventListener('product:variant-change', this._handleVariantChange);
        if (this._scrollObserver) this._scrollObserver.disconnect();
        if (this._ro) this._ro.disconnect();
        if (this._roSettle) clearTimeout(this._roSettle);
      }

      // ----------------------------------------------------------------
      // Warp application
      // ----------------------------------------------------------------
      _waitForArtLayout() {
        var arts = Array.from(this.querySelectorAll('.pp26__art'));
        if (!arts.length) return Promise.resolve();
        var promises = arts.map(function (img) {
          if (img.complete && img.naturalWidth > 0) return Promise.resolve();
          return new Promise(function (res) {
            img.addEventListener('load',  res, { once: true });
            img.addEventListener('error', res, { once: true });
          });
        });
        // Tambem espera os frames carregarem pra detectar o aspect real
        var frames = Array.from(this.querySelectorAll('.pp26__frame'));
        var framePromises = frames.map(function (img) {
          if (img.complete && img.naturalWidth > 0) return Promise.resolve();
          return new Promise(function (res) {
            img.addEventListener('load',  res, { once: true });
            img.addEventListener('error', res, { once: true });
          });
        });
        return Promise.all(promises.concat(framePromises));
      }

      _syncStageAspectToFrame() {
        // Sincroniza o aspect do stage com o PNG da moldura ativa.
        // Garante que o warp calibrado no builder bata com o render aqui.
        var frame = this.querySelector('.pp26__frame:not([hidden])');
        if (!frame || !frame.src) return;
        var self = this;
        var apply = function () {
          if (!frame.naturalWidth) return;
          var stage = frame.closest('.pp26__perspective-stage');
          if (!stage) return;
          // sem-moldura: o frame antigo é 1260×1600 (retrato) e os corners foram
          // calibrados pra stage QUADRADO (como os migrados 1600²) → forçar 1/1
          // evita a arte sair espremida. Demais tipos seguem o aspect do frame.
          var aspect = (frame.dataset.frameType === 'sem-moldura')
            ? '1 / 1'
            : (frame.naturalWidth + ' / ' + frame.naturalHeight);
          stage.style.aspectRatio = aspect;
          // Sempre re-warpa em rAF após o reflow — idempotente (mesma matrix3d),
          // mas necessário pra cobrir caminhos onde aspect já bate mas warp
          // ainda não foi aplicado (boot com frame em cache + variant-change
          // inicial que limpou art.style.transform).
          requestAnimationFrame(self._applyAllWarps);
        };
        if (frame.complete && frame.naturalWidth > 0) {
          apply();
          return;
        }
        frame.addEventListener('load', function onL() {
          frame.removeEventListener('load', onL);
          apply();
        }, { once: true });
      }

      _applyAllWarps() {
        var self = this;
        this._perspectiveStages.forEach(function (stage) {
          self._applyWarpToStage(stage);
        });
      }

      _applyWithRetry(attempt) {
        var self = this;
        requestAnimationFrame(function () {
          // Aplica em todos os stages e checa se algum falhou
          var allOk = true;
          self._perspectiveStages.forEach(function (stage) {
            var ok = self._applyWarpToStage(stage);
            if (!ok) allOk = false;
          });
          // Verifica também: art tem matrix3d no inline style?
          // (sucesso da função pode ter sido true mas style sumiu por outro código)
          var hasMatrix = self._perspectiveStages.every(function (stage) {
            var art = stage.querySelector('.pp26__art');
            var frame = stage.querySelector('.pp26__frame');
            // Se for sem-moldura, não precisa de matrix3d
            if (frame && (frame.hidden || frame.dataset.frameType === 'sem-moldura')) return true;
            return art && art.style.transform && art.style.transform.indexOf('matrix3d') !== -1;
          });
          if ((!allOk || !hasMatrix) && attempt < 10) {
            setTimeout(function () {
              self._syncStageAspectToFrame();
              self._applyWithRetry(attempt + 1);
            }, Math.min(150 * (attempt + 1), 800));
          }
        });
      }

      _applyWarpToStage(stage) {
        var art = stage.querySelector('.pp26__art');
        if (!art) return false;
        // Skip warp se frame oculto (não tem corners pra mapear)
        var frame = stage.querySelector('.pp26__frame');
        if (frame && frame.hidden) return true;
        // Tamanho de LAYOUT (offset*) — não rect — pra o warp não duplicar quando
        // o stage tem transform:scale (moldura maior no mobile). Em desktop/tablet
        // não há transform, então offset == rect.
        var W = stage.offsetWidth, H = stage.offsetHeight;
        if (!W || !H) return false;
        var corners = this._readCorners(stage);
        // Fallback: se data-corners faltar/inválido, deriva do builder/table
        // a partir do dataset do frame ATIVO. Evita boot com warp vazio.
        if (!corners && frame) {
          var t = frame.dataset.frameType;
          var c = frame.dataset.frameColor;
          if (t && t !== 'sem-moldura') {
            corners = this._defaultCorners(this._orientation, t, c);
            if (corners) stage.setAttribute('data-corners', JSON.stringify(corners));
          }
        }
        if (!corners) return false;
        // Corners são % da CAIXA DO FRAME (calibração feita com stage == frame).
        // Quando o stage não bate exatamente com o aspect do frame (ex.: largura
        // clampada por max-width deixa o stage não-quadrado), o frame é
        // object-fit:contain → fica menor e centralizado. Mapear a arte pelo
        // stage cru faria ela vazar. Então calculamos a caixa contida do frame
        // (cw×ch + offset ox,oy) e mapeamos os corners relativos a ela.
        var cw = W, ch = H, ox = 0, oy = 0;
        if (frame && frame.naturalWidth && frame.naturalHeight) {
          var fa = frame.naturalWidth / frame.naturalHeight;
          cw = Math.min(W, H * fa);
          ch = Math.min(H, W / fa);
          ox = (W - cw) / 2;
          oy = (H - ch) / 2;
        }
        var dst = corners.map(function (c) { return [ox + c[0] / 100 * cw, oy + c[1] / 100 * ch]; });
        var src = [[0, 0], [W, 0], [W, H], [0, H]];
        // MAPA CANTO-A-CANTO em TODOS os acabamentos com moldura E no canvas: src =
        // retângulo inteiro da arte → os 4 handles SÃO os 4 cantos literais da arte
        // (sem cover, sem transbordo; mover/travar uma quina não re-deforma as outras).
        // Só sem-moldura usa CONTAIN (arte inteira centralizada + escala).
        var _ft = frame && frame.dataset && frame.dataset.frameType;
        if (_ft === 'sem-moldura' && art.naturalWidth && art.naturalHeight) {
          // CONTAIN: sem moldura não corta nem estica — encaixa a arte INTEIRA na
          // proporção original, centralizada no quad. (O stage não é quadrado, então
          // *fill* no quad em % comprimia a arte na horizontal = ficava fina.)
          var _iaSM = art.naturalWidth / art.naturalHeight;
          var _qw = dst[1][0] - dst[0][0];
          var _qh = dst[3][1] - dst[0][1];
          var _cx = (dst[0][0] + dst[2][0]) / 2;
          var _cy = (dst[0][1] + dst[2][1]) / 2;
          var _fw, _fh;
          if (_iaSM > _qw / _qh) { _fw = _qw; _fh = _qw / _iaSM; }
          else { _fh = _qh; _fw = _qh * _iaSM; }
          // TAMANHO sem-moldura (não-warp): escala a arte em torno do centro.
          var _sms = this._builderData && this._builderData.semMolduraScale && this._builderData.semMolduraScale[this._orientation];
          _sms = (typeof _sms === 'number' && _sms > 0) ? _sms : 1;
          _fw *= _sms; _fh *= _sms;
          dst = [[_cx - _fw / 2, _cy - _fh / 2], [_cx + _fw / 2, _cy - _fh / 2], [_cx + _fw / 2, _cy + _fh / 2], [_cx - _fw / 2, _cy + _fh / 2]];
        }
        var h = solveHomography(src, dst);
        var m = homographyToMatrix3d(h);
        art.style.transformOrigin = '0 0';
        art.style.transform = 'matrix3d(' + m.join(',') + ')';
        // Canvas agora é canto-a-canto igual aos outros (sem clip de curva): a arte é
        // exatamente o quad dos 4 handles. Limpa clip/mask de tentativas anteriores.
        art.style.webkitMaskImage = '';
        art.style.maskImage = '';
        art.style.clipPath = '';
        // Textura de tecido (canvas): warp PRÓPRIO (data-tex-corners). SOLTA da arte:
        // se data-tex-corners não estiver setado (ex.: modo ?calibrate), cai nos
        // cantos próprios da textura do template (textureCorners), NUNCA nos da arte —
        // assim mexer no warp da arte não arrasta a textura junto.
        var tex = this._ensureArtTex(stage);
        if (tex) {
          var texCorners = null;
          var traw = stage.getAttribute('data-tex-corners');
          if (traw) { try { texCorners = JSON.parse(traw); } catch (e) {} }
          if (!texCorners || texCorners.length !== 4) {
            var _bdtc = this._builderData && this._builderData.textureCorners &&
              this._builderData.textureCorners[this._orientation] &&
              this._builderData.textureCorners[this._orientation].canvas;
            texCorners = (_bdtc && _bdtc.length === 4) ? _bdtc : corners;
          }
          var tdst = texCorners.map(function (c) { return [ox + c[0] / 100 * cw, oy + c[1] / 100 * ch]; });
          // SRC PRÓPRIO da textura (retângulo cheio), NÃO o `src` cover-cropado da arte.
          // Com o crop da arte, a homografia extrapolava e a textura "vazava" pra fora
          // dos cantos dela. O linho ladrilha, então mapeia o elemento inteiro -> tdst.
          var _tsrc = [[0, 0], [W, 0], [W, H], [0, H]];
          var tm = homographyToMatrix3d(solveHomography(_tsrc, tdst));
          tex.style.transformOrigin = '0 0';
          tex.style.transform = 'matrix3d(' + tm.join(',') + ')';
          var ftype = frame && frame.dataset.frameType;
          tex.classList.toggle('is-on', ftype === 'canvas');
        }
        return true;
      }

      // Cria (se faltar) a camada de textura logo após a .pp26__art e seta o bg por orientação.
      _ensureArtTex(stage) {
        var tex = stage.querySelector('.pp26__art-tex');
        if (!tex) {
          var art = stage.querySelector('.pp26__art');
          if (!art) return null;
          tex = document.createElement('div');
          tex.className = 'pp26__art-tex';
          var url = this._texUrl();
          if (url) {
            tex.style.backgroundImage = 'url(' + url + ')';
            // REPAINT FIX: com mix-blend-mode:multiply, quando o bg da textura
            // carrega tarde (boot, sem a animação de fade da troca de variante)
            // o compositor às vezes não pinta a camada. Pré-carrega e força um
            // nudge de repaint + re-warp quando a imagem fica disponível.
            var self = this;
            var pre = new Image();
            pre.addEventListener('load', function () {
              self._applyWarpToStage(stage);
              var prev = tex.style.opacity;
              tex.style.opacity = '0.4001';
              requestAnimationFrame(function () { tex.style.opacity = prev; });
            }, { once: true });
            pre.src = url;
          }
          art.insertAdjacentElement('afterend', tex);
        }
        return tex;
      }
      _texUrl() {
        var name = 'pp26-tex-canvas-' + this._orientation + '.webp';
        // Monta a URL do Files trocando o filename na URL de um frame (que é uma
        // URL do Shopify Files). Assim a textura vem do mesmo CDN dos frames.
        var fr = this.querySelector('.pp26__frame');
        if (fr && fr.src) {
          try {
            var u = new URL(fr.src, window.location.origin);
            var p = u.pathname.split('/'); p[p.length - 1] = name;
            u.pathname = p.join('/'); u.search = '';
            return u.toString();
          } catch (e) {}
        }
        return name;
      }

      // === TESTE LOCAL (interno) — caixa robusta de copiar JSON ===
      // clipboard.writeText falha silencioso se o documento não está focado e o
      // prompt() às vezes é bloqueado. Textarea + execCommand('copy') sempre funciona
      // dentro do clique (user gesture), com fallback manual visível.
      _showCopyBox(json, label) {
        var old = document.getElementById('pp26-copybox');
        if (old) old.remove();
        var ov = document.createElement('div');
        ov.id = 'pp26-copybox';
        ov.style.cssText = 'position:fixed;inset:0;z-index:2147483647;background:rgba(0,0,0,.55);display:flex;align-items:center;justify-content:center;';
        var box = document.createElement('div');
        box.style.cssText = 'background:#1e1e1e;color:#eee;padding:14px;border-radius:10px;width:min(560px,92vw);box-shadow:0 10px 40px rgba(0,0,0,.5);font-family:system-ui,sans-serif;';
        var title = document.createElement('div');
        title.textContent = label || 'JSON';
        title.style.cssText = 'font-size:12px;color:#aaa;margin-bottom:6px;text-transform:uppercase;letter-spacing:.05em;';
        var ta = document.createElement('textarea');
        ta.readOnly = true; ta.value = json;
        ta.style.cssText = 'width:100%;height:220px;background:#111;color:#7fdca4;border:1px solid #444;border-radius:6px;padding:8px;font:12px/1.4 ui-monospace,Menlo,monospace;resize:vertical;';
        var bar = document.createElement('div');
        bar.style.cssText = 'display:flex;gap:8px;margin-top:10px;';
        var bcopy = document.createElement('button');
        bcopy.textContent = 'Copiar';
        bcopy.style.cssText = 'flex:1;background:#0a7;border:0;color:#042;font-weight:700;padding:10px;border-radius:6px;cursor:pointer;';
        var bclose = document.createElement('button');
        bclose.textContent = 'Fechar';
        bclose.style.cssText = 'background:#444;border:0;color:#eee;padding:10px 14px;border-radius:6px;cursor:pointer;';
        bar.appendChild(bcopy); bar.appendChild(bclose);
        box.appendChild(title); box.appendChild(ta); box.appendChild(bar);
        ov.appendChild(box); document.body.appendChild(ov);
        ta.focus(); ta.select();
        var doCopy = function () {
          ta.focus(); ta.select();
          var ok = false;
          try { ok = document.execCommand('copy'); } catch (e) {}
          if (navigator.clipboard && navigator.clipboard.writeText) {
            try { navigator.clipboard.writeText(json).then(function(){}, function(){}); } catch (e) {}
          }
          bcopy.textContent = ok ? 'Copiado ✓' : 'Selecione e Cmd+C';
          setTimeout(function () { bcopy.textContent = 'Copiar'; }, 1500);
        };
        bcopy.addEventListener('click', doCopy);
        bclose.addEventListener('click', function () { ov.remove(); });
        ov.addEventListener('click', function (e) { if (e.target === ov) ov.remove(); });
        doCopy();
      }

      _applyInitialOffset() {
        var self = this;
        this._frames.forEach(function (frame) {
          self._applyFrameDims(frame, frame.dataset.frameType, frame.dataset.frameColor);
        });
      }

      // Média dos offsets de todas as cores de um tipo. As molduras de cores
      // diferentes do mesmo tipo são a MESMA geometria (só muda a cor da madeira),
      // então offsets por-cor são ruído de calibração e fazem a moldura escorregar
      // ao trocar de cor. Colapsando pra um valor único por tipo, todas as cores
      // ficam na mesma posição → o crossfade vira puro, sem deslize.
      _avgOffsetForType(map, type) {
        var byColor = map && map[type];
        if (!byColor) return null;
        var colors = Object.keys(byColor).filter(function (k) {
          return byColor[k] && typeof byColor[k] === 'object';
        });
        if (!colors.length) return null;
        var sum = {}, n = colors.length;
        colors.forEach(function (c) {
          var o = byColor[c];
          Object.keys(o).forEach(function (k) {
            if (typeof o[k] === 'number') sum[k] = (sum[k] || 0) + o[k];
          });
        });
        var avg = {};
        Object.keys(sum).forEach(function (k) { avg[k] = sum[k] / n; });
        return avg;
      }

      _applyFrameDims(frame, type, color) {
        // Offset único por tipo (média das cores) — alinha todas as cores na
        // mesma posição pra não escorregar no crossfade. Ver _avgOffsetForType.
        var offset = this._avgOffsetForType(this._frameOffsets, type) ||
                     (this._frameOffsets && this._frameOffsets[type] && this._frameOffsets[type][color]);
        // Block dims — também colapsado pra média por tipo (mesmo motivo do offset:
        // cores são a mesma geometria; per-cor faz a moldura mudar de tamanho/posição).
        var typeDims = this._blockDims && this._blockDims[type];
        var dims = this._avgOffsetForType(this._blockDims, type) ||
                   (typeDims && typeDims[color] ? typeDims[color] : typeDims);
        var ox = (offset && offset.x) || 0;
        var oy = (offset && offset.y) || 0;
        if (dims && dims.ox !== undefined) ox += dims.ox;
        if (dims && dims.oy !== undefined) oy += dims.oy;
        // Nudge fino por tipo (em %) — compensa geração imperfeita do webp.
        // A caixa mostra um sliver à esquerda; empurra todas as cores p/ direita.
        var FRAME_NUDGE_X = { caixa: 1 };
        ox += (FRAME_NUDGE_X[type] || 0);
        // Width: prioriza w do frameOffsets (calibração da régua), fallback no block dim
        var wPct = (offset && offset.w) ? offset.w : (dims && dims.w);
        if (wPct && wPct !== 80) {
          frame.style.width = wPct + '%';
        } else {
          frame.style.width = '';
        }
        // Height: idem
        var hPct = (offset && offset.h) ? offset.h : (dims && dims.h);
        if (hPct && hPct !== 80) {
          frame.style.height = hPct + '%';
        } else {
          frame.style.height = '';
        }
        if (ox || oy) {
          // builder JSON v2 (régua jun26) salva offset em float pequeno = %.
          // Se valor < 30, assume %; senão px (compat com builder antigo em px).
          var unit = (Math.abs(ox) < 30 && Math.abs(oy) < 30) ? '%' : 'px';
          frame.style.translate = ox + unit + ' ' + oy + unit;
        } else {
          frame.style.translate = '';
        }
      }

      _readCorners(stage) {
        var raw = stage.getAttribute('data-corners');
        if (!raw) return null;
        try {
          var parsed = JSON.parse(raw);
          if (!Array.isArray(parsed) || parsed.length !== 4) return null;
          return parsed;
        } catch (e) {
          return null;
        }
      }

      // ----------------------------------------------------------------
      // Variant change → troca o PNG e os corners do frame
      // ----------------------------------------------------------------
      _handleVariantChange(ev) {
        var variant = ev && ev.detail && ev.detail.variant;
        if (!variant || !variant.options) return;
        var info = this._parseVariantOptions(variant.options);
        this._currentType = info.type;
        this._updateFrame(info.type, info.color);
        this._applySizeMulFor(info.type); // sem-moldura pode ter tamanho/X próprio

        // Update environment + frontal slides
        var sizeKey = this._extractSizeFromVariant(variant.options);
        if (sizeKey) this._currentSizeKey = sizeKey;
        this._updateEnvironment(info.type, info.color, this._currentSizeKey);

        this.dispatchEvent(new CustomEvent('pp26:frame-updated', {
          bubbles: true,
          detail: { type: info.type, color: info.color, hasFrame: info.hasFrame, sizeKey: this._currentSizeKey }
        }));
      }

      _parseVariantOptions(options) {
        var type = 'canvas';
        var color = 'mel';
        var hasFrame = true;
        for (var i = 0; i < options.length; i++) {
          var h = String(options[i]).toLowerCase()
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, '-');
          if (h === 'sem-moldura' || h === 'sem_moldura') {
            type = 'sem-moldura';
            // hasFrame=true agora — sem-moldura usa PNG transparente como qualquer outro tipo
            hasFrame = true;
          } else if (h.indexOf('canvas') !== -1) {
            type = 'canvas';
          } else if (h.indexOf('caixa') !== -1 && h.indexOf('paspatur') !== -1) {
            // novo: caixa com paspatur — nome longo bate com os webp no Files
            type = 'caixa-com-paspatur';
          } else if (h.indexOf('slim') !== -1 && h.indexOf('paspatur') !== -1) {
            // novo: slim com paspatur
            type = 'slim-com-paspatur';
          } else if (h.indexOf('paspatur') !== -1) {
            // padrão com paspatur — nome longo bate com os webp no Files.
            // pp26 antigo com metaobject pp26_molduras tem lookup table que
            // ganha prioridade, então mesmo retornando "padrao-com-paspatur"
            // aqui, lookup resolve com a URL certa pros produtos pp26.
            type = 'padrao-com-paspatur';
          } else if (h.indexOf('caixa') !== -1) {
            type = 'caixa';
          } else if (h.indexOf('slim') !== -1) {
            // novo: slim
            type = 'slim';
          } else if (h.indexOf('padrao') !== -1) {
            type = 'padrao';
          }
          if (h === 'mel' || h === 'preta' || h === 'pinus' || h === 'branca') {
            color = h;
          } else if (h === 'madeira-escura') {
            // novo Option Value: mapeia pro mesmo arquivo/lookup key que 'mel'
            color = 'mel';
          } else if (h === 'madeira-clara') {
            // novo Option Value: mapeia pro mesmo arquivo/lookup key que 'pinus'
            color = 'pinus';
          }
        }
        return { type: type, color: color, hasFrame: hasFrame };
      }

      // API pública pra jun26.js (setupHiddenVariantSim) — força update de
      // frame+corners+warp pros tipos ocultos (slim, slim-com-paspatur,
      // caixa-com-paspatur) que não disparam variant change real.
      updateFrame(type, color) { return this._updateFrame(type, color); }

      // Aplica a config de efeito (conf) nas vars CSS do :root, lidas pelas
      // pseudo ::view-transition. Mapa de efeito → nomes dos keyframes.
      _applyFx(conf) {
        if (!conf) return;
        var FX = {
          fade: ['pp26fx-fade-out', 'pp26fx-fade-in'],
          depth: ['pp26fx-depth-out', 'pp26fx-depth-in'],
          tilt: ['pp26fx-tilt-out', 'pp26fx-tilt-in'],
          flip: ['pp26fx-flip-out', 'pp26fx-flip-in'],
          slide: ['pp26fx-slide-out', 'pp26fx-slide-in'],
          zoom: ['pp26fx-zoom-out', 'pp26fx-zoom-in'],
          blur: ['pp26fx-blur-out', 'pp26fx-blur-in']
        };
        var kf = FX[conf.effect] || FX.fade;
        var s = document.documentElement.style;
        s.setProperty('--pp26fx-out', kf[0]);
        s.setProperty('--pp26fx-in', kf[1]);
        if (conf.dur != null) s.setProperty('--pp26fx-dur', conf.dur + 'ms');
        if (conf.ease) s.setProperty('--pp26fx-ease', conf.ease);
        if (conf.depth != null) s.setProperty('--pp26fx-depth', conf.depth + 'px');
        if (conf.angle != null) s.setProperty('--pp26fx-angle', conf.angle + 'deg');
        if (conf.slide != null) s.setProperty('--pp26fx-slide', conf.slide + 'px');
        if (conf.scaleOut != null) s.setProperty('--pp26fx-scale-out', conf.scaleOut);
        if (conf.scaleIn != null) s.setProperty('--pp26fx-scale-in', conf.scaleIn);
        if (conf.blur != null) s.setProperty('--pp26fx-blur', conf.blur + 'px');
        if (conf.persp != null) s.setProperty('--pp26fx-persp', conf.persp + 'px');
        return conf;
      }

      // Aplica o offset de posição do grupo (translate no stage) pro tipo×cor.
      // Lê framePos[ori][type][color] (ou ['*'] = todas as cores), fallback 0.
      _applyFramePos(type, color) {
        var ori = this._orientation;
        var byType = (this._framePos[ori] && this._framePos[ori][type]) || null;
        var p = (byType && (byType[color] || byType['*'])) || { x: 0, y: 0 };
        var px = (p.x || 0), py = (p.y || 0), ps = (p.s != null ? p.s : 1);
        this._perspectiveStages.forEach(function (st) {
          st.style.setProperty('--pp26-posx', px + '%');
          st.style.setProperty('--pp26-posy', py + '%');
          st.style.setProperty('--pp26-poss', ps);
        });
      }

      // DEBOUNCE: uma troca de acabamento/cor dispara uma CASCATA de chamadas
      // (variant-change + handlers do jun26 + reset de cor), em ticks diferentes.
      // Se cada uma rodasse um View Transition, eles se cancelariam (→ sem
      // animação). Coalescemos a rajada num único VT pro ÚLTIMO alvo pedido.
      _updateFrame(type, color) {
        var self = this;
        self._fxPending = { type: type, color: color };
        if (self._fxDebounce) return;
        self._fxDebounce = setTimeout(function () {
          self._fxDebounce = null;
          var p = self._fxPending; self._fxPending = null;
          if (p) self._runTransition(p.type, p.color);
        }, 45);
      }

      _runTransition(type, color) {
        var self = this;
        if (type === 'sem-moldura') color = 'default';
        // NO-OP: se tipo E cor não mudam (ex.: troca de TAMANHO), não há troca de
        // moldura — não roda transição/efeito (nem VT), evitando o "pisca" da
        // página e efeito sem sentido na seleção de tamanho.
        var prevType = (self._frames[0] && self._frames[0].dataset.frameType) || null;
        var prevColor = (self._frames[0] && self._frames[0].dataset.frameColor) || null;
        if (prevType === type && prevColor === color) return;
        // Escolhe o efeito: troca de COR (mesmo tipo) usa a config "color" (soft);
        // troca de MOLDURA usa "frame". Aplica as vars ANTES da transição.
        var _isColorOnly = prevType && prevType === type;
        var _conf = self._fx ? (_isColorOnly ? self._fx.color : self._fx.frame) : null;
        if (_conf) self._applyFx(_conf);

        // commit(): muta o DOM pro estado novo (srcs + corners + textura + warp).
        // instantWarp=true aplica o warp SEM a transição CSS (warp final imediato)
        // — usado dentro do View Transition, que cuida do morph visual. Retorna
        // uma Promise que resolve quando os PNGs novos carregam.
        function commit(instantWarp) {
          var loads = [];
          self._frames.forEach(function (frame) {
            if (!type) { frame.setAttribute('hidden', ''); return; }
            var stageEl = frame.closest('.pp26__perspective-stage');
            if (stageEl) stageEl.classList.remove('pp26__perspective-stage--no-frame');
            var artC = stageEl && stageEl.querySelector('.pp26__art');
            if (artC) artC.classList.remove('pp26__art--no-frame');
            frame.removeAttribute('hidden');
            var orientation = frame.dataset.orientation || self._orientation;
            var newSrc = self._resolveFrameUrl(orientation, type, color, frame.src);
            if (newSrc && newSrc !== frame.src) {
              var im = new Image(); im.src = newSrc;
              loads.push(new Promise(function (res) {
                im.addEventListener('load', res, { once: true });
                im.addEventListener('error', res, { once: true });
                if (im.complete) res();
              }));
              frame.src = newSrc;
            }
            frame.style.opacity = '1';
            frame.style.filter = '';
            frame.dataset.frameType = type;
            frame.dataset.frameColor = color;
            self._applyFrameDims(frame, type, color);
          });
          self._perspectiveStages.forEach(function (stage) {
            var d = self._defaultCorners(self._orientation, type, color);
            if (d) stage.setAttribute('data-corners', JSON.stringify(d));
          });
          if (type === 'canvas' && self._builderData) {
            var _tcd = self._builderData.textureCorners && self._builderData.textureCorners[self._orientation] && self._builderData.textureCorners[self._orientation].canvas;
            var _top = self._builderData.texOpacity && self._builderData.texOpacity[self._orientation];
            self._perspectiveStages.forEach(function (stage) { if (_tcd) stage.setAttribute('data-tex-corners', JSON.stringify(_tcd)); });
            if (_top != null) self.style.setProperty('--pp26-tex-op', _top);
          }
          self._applyFramePos(type, color);
          self._syncStageAspectToFrame();
          if (instantWarp) {
            // desliga a transição do warp (classe --resizing) → snapshot "new" do
            // View Transition já com o warp final; o VT faz o morph (blur).
            self.classList.add('pp26-gallery--resizing');
            self._applyAllWarps();
            self.classList.remove('pp26-gallery--resizing');
          } else {
            requestAnimationFrame(self._applyAllWarps);
          }
          Promise.all(loads).then(function () {
            self._syncStageAspectToFrame();
            requestAnimationFrame(self._applyAllWarps);
          });
          return Promise.all(loads);
        }

        // MOTOR OVERLAY: anima SÓ a moldura (uma camada por cima). A página fica
        // VIVA o tempo todo → o botão/preço não piscam, não "atualizam" nem
        // mostram "indisponível" congelado (tudo isso era o View Transitions
        // fotografando e congelando a página inteira). O efeito vem do cardápio
        // (_conf) aplicado via WAAPI na camada antiga (sai) + na nova (entra).
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        var dur = (_conf && _conf.dur) || 500;
        var ease = (_conf && _conf.ease) || 'cubic-bezier(0.22,1,0.36,1)';
        function fxFrames(c) {
          var d = (c && c.depth) || 140, ang = (c && c.angle) || 12, sl = (c && c.slide) || 60,
            so = (c && c.scaleOut) || 0.9, si = (c && c.scaleIn) || 1.1, bl = (c && c.blur) || 8,
            P3 = 'perspective(' + ((c && c.persp) || 1200) + 'px) ';
          switch (c && c.effect) {
            case 'blur': return { old: [{ opacity: 1, filter: 'blur(0px)' }, { opacity: 0, filter: 'blur(' + bl + 'px)' }], base: [{ filter: 'blur(' + bl + 'px)' }, { filter: 'blur(0px)' }] };
            case 'depth': return { old: [{ opacity: 1, transform: P3 + 'translateZ(0) scale(1)' }, { opacity: 0, transform: P3 + 'translateZ(-' + d + 'px) scale(' + so + ')' }], base: [{ opacity: 0, transform: P3 + 'translateZ(' + d + 'px) scale(' + si + ')' }, { opacity: 1, transform: P3 + 'translateZ(0) scale(1)' }] };
            case 'tilt': return { old: [{ opacity: 1, transform: P3 + 'rotateY(0deg) translateZ(0)' }, { opacity: 0, transform: P3 + 'rotateY(-' + ang + 'deg) translateZ(-40px)' }], base: [{ opacity: 0, transform: P3 + 'rotateY(' + ang + 'deg) translateZ(-40px)' }, { opacity: 1, transform: P3 + 'rotateY(0deg) translateZ(0)' }] };
            case 'flip': return { old: [{ opacity: 1, transform: P3 + 'rotateY(0deg)' }, { opacity: 0, transform: P3 + 'rotateY(-' + ang + 'deg)' }], base: [{ opacity: 0, transform: P3 + 'rotateY(' + ang + 'deg)' }, { opacity: 1, transform: P3 + 'rotateY(0deg)' }] };
            case 'slide': return { old: [{ opacity: 1, transform: 'translateX(0)' }, { opacity: 0, transform: 'translateX(-' + sl + 'px)' }], base: [{ opacity: 0, transform: 'translateX(' + sl + 'px)' }, { opacity: 1, transform: 'translateX(0)' }] };
            case 'zoom': return { old: [{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(' + so + ')' }], base: [{ opacity: 0, transform: 'scale(' + si + ')' }, { opacity: 1, transform: 'scale(1)' }] };
            default: return { old: [{ opacity: 1 }, { opacity: 0 }], base: null }; // fade
          }
        }
        var kf = fxFrames(_conf);
        var olds = self._frames.map(function (frame) { return frame.src; });
        // CROSSFADE PURO DA ARTE: o samba vinha da arte MORFAR (matrix3d interpola
        // "nadando" entre duas homografias). Em vez disso, clonamos a arte (e a
        // textura do canvas) no warp ANTIGO como "fantasma" por cima da arte nova,
        // e sumimos por opacidade. A arte nova entra JÁ no warp final (commit com
        // instantWarp = snap, sem transição). Mesmo padrão de overlay da moldura.
        var artGhosts = [];
        if (!reduce) {
          self._perspectiveStages.forEach(function (stage) {
            stage.querySelectorAll('.pp26__art--xfade, .pp26__art-tex--xfade').forEach(function (o) { o.remove(); });
            var art = stage.querySelector('.pp26__art');
            if (art && art.style.transform) {
              var g = art.cloneNode(false);
              g.className = 'pp26__art pp26__art--xfade';
              g.removeAttribute('id');
              g.style.transition = 'none';
              g.style.zIndex = '1';
              art.parentNode.insertBefore(g, art.nextSibling);
              artGhosts.push(g);
            }
            var tex = stage.querySelector('.pp26__art-tex');
            if (tex && tex.classList.contains('is-on') && tex.style.transform) {
              var gt = tex.cloneNode(false);
              gt.className = 'pp26__art-tex is-on pp26__art-tex--xfade';
              gt.removeAttribute('id');
              gt.style.transition = 'none';
              tex.parentNode.insertBefore(gt, tex.nextSibling);
              artGhosts.push(gt);
            }
          });
        }
        commit(true); // arte/textura NOVAS entram já no warp final (snap, sem morph)
        if (reduce) return; // reduced-motion: troca direta, sem animação
        // ACABAMENTO = FADE-THROUGH (Material): a antiga SAI primeiro (desfocando),
        // a nova ENTRA depois (delay) de foco suave → sensação "some, depois aparece",
        // mais moderna/respirada. COR mantém crossfade simultâneo (arte não se move,
        // já é perfeito). Filtro (blur) não conflita com o matrix3d do warp.
        var _ft2 = !_isColorOnly && _conf && _conf.effect === 'blur';
        var _artBlur = _ft2 ? Math.min(2, (_conf.blur || 8) * 0.5) : 0;
        // overlap GRANDE + delay pequeno = bem suave (a nova entra enquanto a antiga
        // ainda sai → sem "vácuo"; vira um crossfade lento com leve atraso da nova).
        var outDur = _ft2 ? Math.round(dur * 0.70) : dur;
        var inDelay = _ft2 ? Math.round(dur * 0.18) : 0;
        var inDur = dur - inDelay;
        artGhosts.forEach(function (g) {
          var startOp = parseFloat(getComputedStyle(g).opacity) || 1;
          var outKf = _artBlur
            ? [{ opacity: startOp, filter: 'blur(0px)' }, { opacity: 0, filter: 'blur(' + _artBlur + 'px)' }]
            : [{ opacity: startOp }, { opacity: 0 }];
          var ag = g.animate(outKf, { duration: outDur, easing: ease, fill: 'forwards' });
          var clg = function () { if (g.parentNode) g.remove(); };
          ag.onfinish = clg; ag.oncancel = clg;
        });
        if (_ft2) {
          // arte NOVA + textura do canvas (SEMPRE agrupada com a moldura) entram DEPOIS
          // do delay, de opacidade 0→base + foco suave (blur→0). Sem isso a textura
          // aparecia sozinha (sem fade), fora de sincronia com a arte/moldura.
          var fadeInEl = function (el) {
            if (!el) return;
            var baseOp = getComputedStyle(el).opacity || '1';
            // preserva o filter de CSS (drop-shadow da arte — ex.: sombra da folha
            // sem-moldura) DENTRO dos keyframes: o WAAPI substitui o filter inteiro,
            // então sem compor a sombra sumia no fade e "pipocava" no fim (13/jul)
            var baseF = getComputedStyle(el).filter;
            baseF = (baseF && baseF !== 'none') ? baseF + ' ' : '';
            var a = el.animate(
              [{ opacity: 0, filter: baseF + 'blur(' + _artBlur + 'px)' }, { opacity: baseOp, filter: baseF + 'blur(0px)' }],
              { duration: inDur, delay: inDelay, easing: ease, fill: 'backwards' });
            var clr = function () { el.style.filter = ''; el.style.opacity = ''; };
            a.onfinish = clr; a.oncancel = clr;
          };
          self._perspectiveStages.forEach(function (stage) {
            fadeInEl(stage.querySelector('.pp26__art:not(.pp26__art--xfade)'));
            var nt = stage.querySelector('.pp26__art-tex:not(.pp26__art-tex--xfade)');
            if (nt && nt.classList.contains('is-on')) fadeInEl(nt);
          });
        }
        self._frames.forEach(function (frame, i) {
          var oldSrc = olds[i];
          if (!oldSrc || oldSrc === frame.src) return; // esse frame não mudou
          var stageEl = frame.closest('.pp26__perspective-stage');
          if (stageEl) stageEl.querySelectorAll('.pp26__frame--xfade').forEach(function (o) { o.remove(); });
          var overlay = frame.cloneNode(false);
          overlay.className = 'pp26__frame--xfade';
          overlay.removeAttribute('hidden'); overlay.removeAttribute('data-frame-type'); overlay.removeAttribute('data-frame-color');
          overlay.src = oldSrc;
          overlay.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;pointer-events:none;z-index:3;opacity:1;will-change:transform,opacity,filter;';
          if (frame.style.transform) overlay.style.transform = frame.style.transform;
          frame.parentNode.insertBefore(overlay, frame.nextSibling);
          var aOld = overlay.animate(kf.old, { duration: outDur, easing: ease, fill: 'forwards' });
          var cl = function () { if (overlay.parentNode) overlay.remove(); };
          aOld.onfinish = cl; aOld.oncancel = cl;
          if (_ft2) {
            // moldura NOVA entra depois (fade-through): opacidade 0→1 + desfoque
            var aIn = frame.animate(
              [{ opacity: 0, filter: 'blur(' + (_conf.blur || 8) + 'px)' }, { opacity: 1, filter: 'blur(0px)' }],
              { duration: inDur, delay: inDelay, easing: ease, fill: 'backwards' });
            var clrf = function () { frame.style.filter = ''; frame.style.opacity = ''; };
            aIn.onfinish = clrf; aIn.oncancel = clrf;
          } else if (kf.base) {
            var aBase = frame.animate(kf.base, { duration: dur, easing: ease });
            var clr = function () { frame.style.filter = ''; };
            aBase.onfinish = clr; aBase.oncancel = clr;
          }
        });
      }

      _parseFrameLookup(raw) {
        if (!raw) return null;
        try {
          var parsed = JSON.parse(raw);
          return parsed && typeof parsed === 'object' ? parsed : null;
        } catch (e) {
          return null;
        }
      }

      // BYPASS LOOKUP: assets que devem IGNORAR o metaobject e usar direto
      // o Shopify Files. O VALOR pode ser true (usa convenção de nome) ou
      // string (substitui o filename — útil quando o asset foi renomeado
      // pra forçar invalidação de cache do CDN).
      // Quando um asset é re-subido e o CDN não invalida, renomeia-se
      // pra ...-v2 e adiciona-se aqui o mapeamento pro novo nome.
      _shouldBypassLookup(orientation, type, color) {
        var key = orientation + '|' + type + '|' + color;
        // === TESTE LOCAL 2jun26 final (interno) ===
        // Tipos já migrados p/ 2jun26 (1600², naming com cor da PDP), servidos do /assets.
        var _MIG = {
          'horizontal|canvas': 1, 'horizontal|caixa': 1,
          'horizontal|padrao': 1, 'horizontal|padrao-com-paspatur': 1,
          'vertical|canvas': 1, 'vertical|caixa': 1,
          'vertical|padrao': 1, 'vertical|padrao-com-paspatur': 1,
          'quadrado|canvas': 1, 'quadrado|caixa': 1,
          'quadrado|padrao': 1, 'quadrado|padrao-com-paspatur': 1
        };
        if (_MIG[orientation + '|' + type]) {
          var _cmap = { mel: 'madeira-escura', pinus: 'madeira-clara', preta: 'preta', branca: 'branca' };
          // Madeira clara (pinus) e escura (mel) re-subidas com NOME NOVO '6jun26-'
          // (bust de cache, sem sobrescrever as 2jun26 antigas no Files). Preta/branca
          // seguem '2jun26-'.
          var _pref = (color === 'mel' || color === 'pinus') ? '6jun26-' : '2jun26-';
          var _nm = _pref + orientation + '-' + type + '-' + (_cmap[color] || color) + '.webp';
          // Frames re-subidos com NOME NOVO após corrigir alinhamento/tamanho da
          // abertura (a CDN cacheava o nome antigo). Mapa nome-antigo → nome-novo.
          // 7jun26- = alinhamento da abertura; 8jun26- = tamanho da abertura (caixa).
          var _REUP = {
            '6jun26-vertical-padrao-madeira-clara.webp': '7jun26-vertical-padrao-madeira-clara.webp',
            '2jun26-vertical-padrao-preta.webp': '7jun26-vertical-padrao-preta.webp',
            '2jun26-vertical-padrao-branca.webp': '7jun26-vertical-padrao-branca.webp',
            '2jun26-vertical-caixa-branca.webp': '7jun26-vertical-caixa-branca.webp',
            '6jun26-vertical-padrao-com-paspatur-madeira-clara.webp': '7jun26-vertical-padrao-com-paspatur-madeira-clara.webp',
            '2jun26-vertical-padrao-com-paspatur-preta.webp': '7jun26-vertical-padrao-com-paspatur-preta.webp',
            '2jun26-vertical-padrao-com-paspatur-branca.webp': '7jun26-vertical-padrao-com-paspatur-branca.webp',
            '2jun26-quadrado-padrao-com-paspatur-preta.webp': '7jun26-quadrado-padrao-com-paspatur-preta.webp',
            // caixa vertical clara/preta re-encaixadas pra abertura da escura (tamanho único)
            '6jun26-vertical-caixa-madeira-clara.webp': '8jun26-vertical-caixa-madeira-clara.webp',
            '2jun26-vertical-caixa-preta.webp': '8jun26-vertical-caixa-preta.webp',
            // canvas vertical preta re-encaixada (estava ~9px à direita das outras cores)
            '2jun26-vertical-canvas-preta.webp': '8jun26-vertical-canvas-preta.webp',
            // horizontal padrão + caixa: cores re-encaixadas na escura (samba ~20px)
            '6jun26-horizontal-padrao-madeira-clara.webp': '8jun26-horizontal-padrao-madeira-clara.webp',
            '2jun26-horizontal-padrao-preta.webp': '8jun26-horizontal-padrao-preta.webp',
            '2jun26-horizontal-padrao-branca.webp': '8jun26-horizontal-padrao-branca.webp',
            '6jun26-horizontal-caixa-madeira-clara.webp': '8jun26-horizontal-caixa-madeira-clara.webp',
            '2jun26-horizontal-caixa-preta.webp': '8jun26-horizontal-caixa-preta.webp',
            '2jun26-horizontal-caixa-branca.webp': '8jun26-horizontal-caixa-branca.webp',
            // horizontal paspatur: cores re-encaixadas na escura (samba ~10px)
            '6jun26-horizontal-padrao-com-paspatur-madeira-clara.webp': '8jun26-horizontal-padrao-com-paspatur-madeira-clara.webp',
            '2jun26-horizontal-padrao-com-paspatur-preta.webp': '8jun26-horizontal-padrao-com-paspatur-preta.webp',
            '2jun26-horizontal-padrao-com-paspatur-branca.webp': '8jun26-horizontal-padrao-com-paspatur-branca.webp',
            // quadrado padrão/caixa/paspatur: cores re-encaixadas na escura (samba ~15-18px)
            '6jun26-quadrado-padrao-madeira-clara.webp': '8jun26-quadrado-padrao-madeira-clara.webp',
            '2jun26-quadrado-padrao-preta.webp': '8jun26-quadrado-padrao-preta.webp',
            '2jun26-quadrado-padrao-branca.webp': '8jun26-quadrado-padrao-branca.webp',
            '2jun26-quadrado-caixa-preta.webp': '8jun26-quadrado-caixa-preta.webp',
            '2jun26-quadrado-caixa-branca.webp': '8jun26-quadrado-caixa-branca.webp',
            '2jun26-quadrado-padrao-com-paspatur-branca.webp': '8jun26-quadrado-padrao-com-paspatur-branca.webp'
          };
          if (_REUP[_nm]) _nm = _REUP[_nm];
          return _nm;
        }
        // === FIM TESTE LOCAL 2jun26 ===
        var BYPASS = {
          'vertical|caixa|branca': 'jun26-vertical-caixa-branca-v2.webp',
          // Pinus (madeira clara) re-subido em v2 — força invalidação de cache do CDN.
          'horizontal|canvas|pinus': 'jun26-horizontal-canvas-pinus-v2.webp',
          'horizontal|padrao|pinus': 'jun26-horizontal-padrao-pinus-v2.webp',
          'horizontal|padrao-com-paspatur|pinus': 'jun26-horizontal-padrao-com-paspatur-pinus-v2.webp',
          'horizontal|caixa|pinus': 'jun26-horizontal-caixa-pinus-v2.webp',
          'horizontal|caixa-com-paspatur|pinus': 'jun26-horizontal-caixa-com-paspatur-pinus-v2.webp',
          'horizontal|slim|pinus': 'jun26-horizontal-slim-pinus-v2.webp',
          'horizontal|slim-com-paspatur|pinus': 'jun26-horizontal-slim-com-paspatur-pinus-v2.webp',
          'vertical|canvas|pinus': 'jun26-vertical-canvas-pinus-v2.webp',
          'vertical|padrao|pinus': 'jun26-vertical-padrao-pinus-v2.webp',
          'vertical|padrao-com-paspatur|pinus': 'jun26-vertical-padrao-com-paspatur-pinus-v2.webp',
          'vertical|caixa|pinus': 'jun26-vertical-caixa-pinus-v2.webp',
          'vertical|caixa-com-paspatur|pinus': 'jun26-vertical-caixa-com-paspatur-pinus-v2.webp',
          'vertical|slim|pinus': 'jun26-vertical-slim-pinus-v2.webp',
          'vertical|slim-com-paspatur|pinus': 'jun26-vertical-slim-com-paspatur-pinus-v2.webp',
          'quadrado|canvas|pinus': 'jun26-quadrado-canvas-pinus-v2.webp',
          'quadrado|padrao|pinus': 'jun26-quadrado-padrao-pinus-v2.webp',
          'quadrado|padrao-com-paspatur|pinus': 'jun26-quadrado-padrao-com-paspatur-pinus-v2.webp',
          'quadrado|caixa|pinus': 'jun26-quadrado-caixa-pinus-v2.webp',
          'quadrado|caixa-com-paspatur|pinus': 'jun26-quadrado-caixa-com-paspatur-pinus-v2.webp',
          'quadrado|slim|pinus': 'jun26-quadrado-slim-pinus-v2.webp',
          'quadrado|slim-com-paspatur|pinus': 'jun26-quadrado-slim-com-paspatur-pinus-v2.webp',
          // Sem moldura — vertical re-subido em v2 (horizontal/quadrado são novos, sem cache).
          'vertical|sem-moldura|default': 'jun26-vertical-sem-moldura-default-v2.webp',
          // MEL — migração quadrada (teste): frames 1600x1600 com placement do cockpit.
          'horizontal|canvas|mel': 'jun26-horizontal-canvas-mel-v2.webp',
          'horizontal|padrao|mel': 'jun26-horizontal-padrao-mel-v2.webp',
          'horizontal|padrao-com-paspatur|mel': 'jun26-horizontal-padrao-com-paspatur-mel-v2.webp',
          'horizontal|caixa|mel': 'jun26-horizontal-caixa-mel-v2.webp',
          'horizontal|caixa-com-paspatur|mel': 'jun26-horizontal-caixa-com-paspatur-mel-v2.webp',
          'horizontal|slim|mel': 'jun26-horizontal-slim-mel-v2.webp',
          'horizontal|slim-com-paspatur|mel': 'jun26-horizontal-slim-com-paspatur-mel-v2.webp',
          'vertical|canvas|mel': 'jun26-vertical-canvas-mel-v2.webp',
          'vertical|padrao|mel': 'jun26-vertical-padrao-mel-v2.webp',
          'vertical|padrao-com-paspatur|mel': 'jun26-vertical-padrao-com-paspatur-mel-v2.webp',
          'vertical|caixa|mel': 'jun26-vertical-caixa-mel-v2.webp',
          'vertical|caixa-com-paspatur|mel': 'jun26-vertical-caixa-com-paspatur-mel-v2.webp',
          'vertical|slim|mel': 'jun26-vertical-slim-mel-v2.webp',
          'vertical|slim-com-paspatur|mel': 'jun26-vertical-slim-com-paspatur-mel-v2.webp',
          'quadrado|canvas|mel': 'jun26-quadrado-canvas-mel-v2.webp',
          'quadrado|padrao|mel': 'jun26-quadrado-padrao-mel-v2.webp',
          'quadrado|padrao-com-paspatur|mel': 'jun26-quadrado-padrao-com-paspatur-mel-v2.webp',
          'quadrado|caixa|mel': 'jun26-quadrado-caixa-mel-v2.webp',
          'quadrado|caixa-com-paspatur|mel': 'jun26-quadrado-caixa-com-paspatur-mel-v2.webp',
          'quadrado|slim|mel': 'jun26-quadrado-slim-mel-v2.webp',
          'quadrado|slim-com-paspatur|mel': 'jun26-quadrado-slim-com-paspatur-mel-v2.webp',
          'horizontal|sem-moldura|default': 'jun26-horizontal-sem-moldura-default-v2.webp',
          'quadrado|sem-moldura|default': 'jun26-quadrado-sem-moldura-default-v2.webp'
        };
        return Object.prototype.hasOwnProperty.call(BYPASS, key) ? BYPASS[key] : false;
      }

      _resolveFrameUrl(orientation, type, color, currentSrc) {
        var bypass = this._shouldBypassLookup(orientation, type, color);
        // Prioridade 1: lookup table do metaobject (URLs Shopify CDN otimizadas)
        // — exceto pra assets na lista de bypass, que vão direto pra convenção.
        if (!bypass && this._frameLookup && this._frameLookup[type] && this._frameLookup[type][color]) {
          return this._frameLookup[type][color];
        }
        // Prioridade 2 (fallback): substitui o nome do arquivo na URL atual.
        // So funciona quando o source e file_url (filename convencional na URL).
        // Bypass pode ser string com nome custom (asset renomeado) ou true (usa convenção).
        var newName = (typeof bypass === 'string')
          ? bypass
          : (this._framePrefix + '-' + orientation + '-' + type + '-' + color + '.webp');
        try {
          var url = new URL(currentSrc, window.location.origin);
          // (01/10) Página aberta em sem-moldura nasce com src="" → currentSrc é a
          // própria URL da PDP (/products/...). Sem a pasta de Files como base,
          // toda moldura escolhida depois vira 404.
          if (url.pathname.indexOf('/cdn/shop/files/') === -1) {
            url = new URL('/cdn/shop/files/x', window.location.origin);
          }
          var parts = url.pathname.split('/');
          parts[parts.length - 1] = newName;
          url.pathname = parts.join('/');
          // Pra bypass: tira o ?v=... (cache key da versão antiga) — Shopify CDN
          // serve a versão mais recente pelo nome canônico sem essa query.
          if (bypass) url.search = '';
          return url.toString();
        } catch (e) {
          return null;
        }
      }

      // Defaults espelhados do pp26-gallery.liquid (servem como fallback no
      // client quando trocar variante). Estrutura: por orientation > type, com
      // _base (acabamento level, propaga pras 4 cores). Offsets por cor são
      // aplicados via translate CSS no frame PNG, não nos corners.
      _defaultCorners(orientation, type, color) {
        // 1. Builder JSON tem prioridade
        if (this._builderData) {
          var bd = this._builderData;
          // Color override
          var co = bd.colorOverrides && bd.colorOverrides[orientation] &&
                   bd.colorOverrides[orientation][type] && bd.colorOverrides[orientation][type][color];
          if (co && co.length === 4) return co;
          // Acabamento corners (base do tipo)
          var ac = bd.acabamentoCorners && bd.acabamentoCorners[orientation] &&
                   bd.acabamentoCorners[orientation][type];
          if (ac && ac.length === 4) return ac;
        }
        // 2. Fallback: tabela hardcoded (legado)
        var table = {
          vertical: {
            canvas:   {
              _base: [[28.54,10.55],[90.55,13.38],[90.21,87.94],[28.5,90.15]],
              mel: [[27.95,10.45],[90.55,13.38],[90.21,87.94],[27.89,90.15]],
              pinus: [[27.01,10.39],[89.14,13.48],[88.88,87.48],[26.58,90.09]],
              branca: [[27.85,10.31],[90.2,12.99],[90.21,87.94],[27.57,90.31]]
            },
            padrao:   { _base: [[19.51,13.87],[79.5,16.53],[79.06,87.64],[19.69,90.11]] },
            caixa:    { _base: [[19.51,11.41],[79.04,14.17],[78.75,86.61],[19.19,89]] },
            paspatur: { _base: [[23.61,15.23],[78.23,17.75],[77.98,83.06],[23.7,85.06]] },
            // sem-moldura: PNG transparente — arte ocupa quase tudo, calibrado
            // depois ajustado via régua. Default centrado com leve margem.
            'sem-moldura': { _base: [[8,8],[92,8],[92,92],[8,92]] }
          },
          horizontal: {
            canvas:   { _base: [[17.83,6.8],[95.8,10.77],[95.46,85.53],[17.52,88.31]] },
            padrao:   { _base: [[14.64,11.48],[89.73,14.82],[89.74,86.33],[14.39,89.49]] },
            caixa:    { _base: [[18.24,14.13],[87.3,17.67],[87.34,86.07],[18.1,89.06]] },
            paspatur: { _base: [[20.74,16.61],[86.82,19.36],[86.83,83.21],[20.31,85.54]] }
          },
          quadrado: {
            canvas:   { _base: [[7,7],[93,7],[93,93],[7,93]] },
            padrao:   { _base: [[7,7],[93,7],[93,93],[7,93]] },
            caixa:    { _base: [[7,7],[93,7],[93,93],[7,93]] },
            paspatur: { _base: [[12,12],[88,12],[88,88],[12,88]] }
          }
        };
        var tipoEntry = table[orientation] && table[orientation][type];
        if (!tipoEntry) return null;
        return tipoEntry[color] || tipoEntry._base;
      }

      // ----------------------------------------------------------------
      // Preload — carrega todas as molduras em background pra troca instantanea
      // ----------------------------------------------------------------
      _preloadFrames() {
        var self = this;
        // Só os acabamentos que TÊM frame. slim, slim-com-paspatur e
        // caixa-com-paspatur não têm webp (404) — preloadá-los gastava banda à toa
        // e atrasava o load das molduras reais (delay na 1ª troca). Preload tira a
        // janela de load entre troca de pill/cor, evita flash sem-warp.
        var ALL_TYPES = ['canvas', 'padrao', 'padrao-com-paspatur', 'caixa'];
        var ALL_COLORS = ['mel', 'preta', 'pinus', 'branca'];
        // sem-moldura tem 1 "cor" virtual = default (PNG transparente único)
        var SEM_MOLDURA = { type: 'sem-moldura', color: 'default' };
        var orient = this._orientation;
        var lookup = this._frameLookup || {};
        // Base path do frame atual pra construir URLs irmãs via convenção de nome
        var baseImg = this._frames && this._frames[0];
        var baseUrl = null;
        if (baseImg && baseImg.src) {
          try {
            var u = new URL(baseImg.src, window.location.origin);
            var parts = u.pathname.split('/');
            parts.pop();
            baseUrl = u.origin + parts.join('/') + '/';
          } catch (e) { /* ignora */ }
        }
        var preloadOne = function (tipo, cor) {
          // Usa o MESMO resolve do render (aplica o _MIG → 2jun26 no Files) pra
          // pré-carregar os arquivos CORRETOS — antes preloadava o naming antigo
          // (jun26-*), então os migrados carregavam frios na 1ª troca (= lag).
          if (baseImg && baseImg.src) {
            var r = self._resolveFrameUrl(orient, tipo, cor, baseImg.src);
            if (r) return r;
          }
          var url = lookup[tipo] && lookup[tipo][cor];
          if (!url && baseUrl) {
            url = baseUrl + self._framePrefix + '-' + orient + '-' + tipo + '-' + cor + '.webp';
          }
          return url;
        };
        var doPreload = function () {
          var seen = {};
          ALL_TYPES.forEach(function (tipo) {
            ALL_COLORS.forEach(function (cor) {
              var url = preloadOne(tipo, cor);
              if (url && !seen[url]) { seen[url] = 1; (new Image()).src = url; }
            });
          });
          // sem-moldura — só 1 webp (cor='default')
          var smUrl = preloadOne(SEM_MOLDURA.type, SEM_MOLDURA.color);
          if (smUrl && !seen[smUrl]) { seen[smUrl] = 1; (new Image()).src = smUrl; }
        };
        if (window.requestIdleCallback) {
          requestIdleCallback(doPreload);
        } else {
          setTimeout(doPreload, 1000);
        }
      }

      // ----------------------------------------------------------------
      // Dots + scroll-snap (IntersectionObserver)
      // ----------------------------------------------------------------
      _initDots() {
        var self = this;
        if (!this._slider) return;

        // Gera dots dinamicamente baseado no numero de slides renderizados
        var dotsContainer = this.querySelector('[data-pp26-dots]');
        if (dotsContainer && this._slides.length >= 2) {
          dotsContainer.innerHTML = '';
          this._slides.forEach(function (slide, i) {
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'pp26-gallery__dot' + (i === 0 ? ' pp26-gallery__dot--active' : '');
            btn.dataset.slide = i;
            btn.setAttribute('aria-label', 'Slide ' + (i + 1));
            dotsContainer.appendChild(btn);
          });
          this._dots = Array.from(dotsContainer.querySelectorAll('.pp26-gallery__dot'));
        }

        if (this._dots.length < 2) return;

        // Click nos dots → scroll suave
        this._dots.forEach(function (dot, i) {
          dot.addEventListener('click', function () {
            var idx = parseInt(dot.dataset.slide, 10) || 0;
            self._goToSlide(idx);
          });
        });

        // IntersectionObserver pra sync de dots + title card
        this._scrollObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var slide = entry.target;
            var idx = parseInt(slide.dataset.slideIndex, 10) || 0;
            self._setActiveDot(idx);
            if (self._titleCard) {
              self._titleCard.classList.toggle('is-hidden', idx >= 1);
            }
            self.dispatchEvent(new CustomEvent('pp26:slide-change', {
              bubbles: true,
              detail: { index: idx }
            }));
          });
        }, { root: this._slider, threshold: 0.6 });

        this._slides.forEach(function (slide) {
          self._scrollObserver.observe(slide);
        });
      }

      _setActiveDot(idx) {
        this._dots.forEach(function (dot, i) {
          dot.classList.toggle('pp26-gallery__dot--active', i === idx);
        });
      }

      _goToSlide(idx) {
        var slide = this._slides[idx];
        if (!slide) return;
        this._slider.scrollTo({
          left: slide.offsetLeft,
          behavior: 'smooth'
        });
      }

      // ----------------------------------------------------------------
      // Utils
      // ----------------------------------------------------------------
      _debounce(fn, ms) {
        var t;
        return function () {
          var ctx = this, args = arguments;
          clearTimeout(t);
          t = setTimeout(function () { fn.apply(ctx, args); }, ms);
        };
      }

      // ================================================================
      // ENVIRONMENT SLIDE — config-driven sizing + frame overlay
      // ================================================================

      _initEnvironment() {
        var envSlide = this.querySelector('.pp26__slide--environment');
        if (!envSlide) return;
        this._envSlide = envSlide;
        this._envArt = envSlide.querySelector('.pp26__env-art');
        this._envFrame = envSlide.querySelector('.pp26__env-frame');
        this._envContainer = envSlide.querySelector('.pp26__env-container');

        // Parse ambiente config
        var raw = this.dataset.ambienteConfig;
        if (!raw) return;
        try {
          this._ambienteConfig = JSON.parse(raw);
        } catch (e) {
          console.warn('pp26: ambiente config parse error', e);
          return;
        }

        // Apply initial position from config
        var cfg = this._ambienteConfig;
        var ori = this._orientation;
        var pos = cfg.artwork && cfg.artwork.position && cfg.artwork.position[ori];
        if (pos && this._envContainer) {
          this._envContainer.style.setProperty('--env-art-top', (pos.top || 50) + '%');
          this._envContainer.style.setProperty('--env-art-left', (pos.left || 50) + '%');
          this._envContainer.style.setProperty('--env-art-tx', (pos.tx || -50) + '%');
          this._envContainer.style.setProperty('--env-art-ty', (pos.ty || -65) + '%');
        }
        if (cfg.containerAspect && this._envContainer) {
          this._envContainer.style.setProperty('--env-aspect', cfg.containerAspect);
        }

        // Resolve initial frame overlay + art size
        this._currentSizeKey = this._extractSizeFromDOM();
        this._updateEnvironment(
          this._frames[0] ? this._frames[0].dataset.frameType : 'canvas',
          this._frames[0] ? this._frames[0].dataset.frameColor : 'mel',
          this._currentSizeKey
        );

        // Init frontal slide too
        this._initFrontal();
      }

      _initFrontal() {
        var frontalSlide = this.querySelector('.pp26__slide--frontal');
        if (!frontalSlide) return;
        this._frontalSlide = frontalSlide;
        this._frontalFrame = frontalSlide.querySelector('.pp26__frontal-frame');
        this._frontalNoFrame = frontalSlide.querySelector('.pp26__frontal-no-frame');

        // Set initial frame image (same as environment)
        if (this._frontalFrame && this._envFrame && this._envFrame.src) {
          this._frontalFrame.src = this._envFrame.src;
        }
      }

      _extractSizeFromDOM() {
        // Try to find the currently selected size from variant picker
        var sizeFieldset = document.querySelector('[data-handle*="tamanho"] input:checked, [data-handle*="tamanho"] .active input');
        if (sizeFieldset) {
          return this._normalizeSize(sizeFieldset.value);
        }
        // Fallback: try all fieldset radios
        var radios = document.querySelectorAll('.product-form__input input[type="radio"]:checked');
        for (var i = 0; i < radios.length; i++) {
          var val = radios[i].value;
          if (/\d+\s*x\s*\d+/.test(val)) {
            return this._normalizeSize(val);
          }
        }
        return null;
      }

      _normalizeSize(raw) {
        if (!raw) return null;
        return String(raw).replace(/\s/g, '').replace(/[,\.]/g, 'x');
      }

      _extractSizeFromVariant(options) {
        if (!options) return null;
        for (var i = 0; i < options.length; i++) {
          var v = String(options[i]).trim();
          if (/\d+\s*x\s*\d+/.test(v)) {
            return this._normalizeSize(v);
          }
        }
        return null;
      }

      _resolveEnvFrameUrl(ori, tipo, cor, sizeKey) {
        var cfg = this._ambienteConfig;
        if (!cfg || !cfg.frameUrls) return '';
        var urls = cfg.frameUrls;
        var sizeEntry = sizeKey && cfg.sizes && cfg.sizes[sizeKey];
        var thickness = sizeEntry ? sizeEntry.thickness : 'v1';

        // Resolve orientation name (quadrado may map to "quadrada")
        var oriName = ori;
        if (urls.oriMap && urls.oriMap[ori]) {
          oriName = urls.oriMap[ori];
        }

        // Choose pattern based on thickness + tipo
        var useV2 = thickness === 'v2';
        if (tipo === 'canvas' && !urls.canvasHasV2) {
          useV2 = false;
        }

        var pattern = useV2 ? urls.patternV2 : urls.pattern;
        var filename = pattern
          .replace('{ori}', oriName)
          .replace('{tipo}', tipo)
          .replace('{cor}', cor);

        return (urls.base || '') + filename;
      }

      _updateEnvironment(type, color, sizeKey) {
        if (!this._ambienteConfig || !this._envContainer) return;
        var cfg = this._ambienteConfig;
        var ori = this._orientation;

        // Update art width
        if (sizeKey && cfg.sizes && cfg.sizes[sizeKey]) {
          var sizeEntry = cfg.sizes[sizeKey];
          var artW = sizeEntry.artWidth;
          var width = (artW && artW[ori]) || (artW && artW['default']) || 30;
          this._envContainer.style.setProperty('--env-art-w', width + '%');

          // Update frame overlay position
          var frame = sizeEntry.frame && sizeEntry.frame[ori];
          if (frame) {
            this._envContainer.style.setProperty('--env-frame-w', frame.w + '%');
            this._envContainer.style.setProperty('--env-frame-tx', frame.tx + '%');
            this._envContainer.style.setProperty('--env-frame-ty', frame.ty + '%');
          }
        }

        // Update frame overlay image
        if (type && type !== 'sem-moldura' && color) {
          var frameUrl = this._resolveEnvFrameUrl(ori, type, color, sizeKey);
          if (this._envFrame) {
            if (frameUrl && frameUrl !== this._envFrame.src) {
              this._envFrame.src = frameUrl;
            }
            this._envFrame.removeAttribute('hidden');
            this._envFrame.dataset.frameType = type;
            this._envFrame.dataset.frameColor = color;
          }
          // Update frontal frame too
          if (this._frontalFrame && frameUrl) {
            this._frontalFrame.src = frameUrl;
            this._frontalFrame.alt = 'Moldura ' + type + ' ' + color;
            this._frontalFrame.removeAttribute('hidden');
          }
          if (this._frontalNoFrame) this._frontalNoFrame.style.display = 'none';
        } else {
          // Sem moldura
          if (this._envFrame) this._envFrame.setAttribute('hidden', '');
          if (this._frontalFrame) this._frontalFrame.setAttribute('hidden', '');
          if (this._frontalNoFrame) this._frontalNoFrame.style.display = '';
        }
      }

      // ================================================================
      // CALIBRATION MODE
      // ================================================================

      _initCalibration() {
        // (interno) data-calibrate-enabled dispensado: ?calibrate basta.
        if (!new URLSearchParams(window.location.search).has('calibrate')) return;

        this.classList.add('pp26--calibrating');
        var self = this;
        var ori = this._orientation;

        // State
        var TIPOS = ['canvas', 'padrao', 'caixa', 'padrao-com-paspatur'];
        var TIPO_LABELS = { canvas: 'Canvas', padrao: 'Padrão', caixa: 'Caixa', 'padrao-com-paspatur': 'Padrão+Pasp.' };
        // Slugs internos mel/pinus MANTIDOS (são chaves de colorOverrides/frameOffsets
        // e do _cmap que resolve o arquivo: pinus->madeira-clara, mel->madeira-escura).
        // Só o rótulo exibido muda pros nomes da PDP. Ordem: clara primeiro (default).
        var CORES = ['pinus', 'preta', 'mel', 'branca'];
        var COR_LABELS = { pinus: 'Madeira clara', preta: 'Preta', mel: 'Madeira escura', branca: 'Branca' };

        var calState = {
          tipo: TIPOS[0],
          cor: CORES[0],
          acabamentoCorners: {},
          colorOverrides: {},
          frameOffsets: {},
          overrideActive: false,
          undoStack: [],
          locked: {}
        };

        // Initialize from builder data or defaults
        if (this._builderData) {
          calState.acabamentoCorners = JSON.parse(JSON.stringify(this._builderData.acabamentoCorners || {}));
          calState.colorOverrides = JSON.parse(JSON.stringify(this._builderData.colorOverrides || {}));
          calState.frameOffsets = JSON.parse(JSON.stringify(this._builderData.frameOffsets || {}));
          calState.textureCorners = JSON.parse(JSON.stringify(this._builderData.textureCorners || {}));
          calState.semMolduraScale = JSON.parse(JSON.stringify(this._builderData.semMolduraScale || {}));
        }
        if (!calState.textureCorners) calState.textureCorners = {};
        // Ensure orientation exists
        if (!calState.acabamentoCorners[ori]) calState.acabamentoCorners[ori] = {};
        if (!calState.colorOverrides[ori]) calState.colorOverrides[ori] = {};
        if (!calState.frameOffsets[ori]) calState.frameOffsets[ori] = {};

        // Ensure defaults for all tipos
        TIPOS.forEach(function(t) {
          if (!calState.acabamentoCorners[ori][t]) {
            var d = self._defaultCorners(ori, t, CORES[0]);
            calState.acabamentoCorners[ori][t] = d || [[8,6],[92,6],[92,94],[8,94]];
          }
          if (!calState.colorOverrides[ori][t]) calState.colorOverrides[ori][t] = {};
          if (!calState.frameOffsets[ori][t]) calState.frameOffsets[ori][t] = {};
        });

        function getCorners() {
          var t = calState.tipo, c = calState.cor;
          if (t === 'textura') {
            if (!calState.textureCorners[ori]) calState.textureCorners[ori] = {};
            if (!calState.textureCorners[ori].canvas) {
              var base = (calState.acabamentoCorners[ori] && calState.acabamentoCorners[ori].canvas) || [[8,6],[92,6],[92,94],[8,94]];
              calState.textureCorners[ori].canvas = base.map(function(p) { return [p[0], p[1]]; });
            }
            return calState.textureCorners[ori].canvas;
          }
          if (calState.overrideActive && calState.colorOverrides[ori][t][c]) {
            return calState.colorOverrides[ori][t][c];
          }
          return calState.acabamentoCorners[ori][t];
        }
        function setCorners(corners) {
          var t = calState.tipo, c = calState.cor;
          if (t === 'textura') {
            if (!calState.textureCorners[ori]) calState.textureCorners[ori] = {};
            calState.textureCorners[ori].canvas = corners.map(function(p) { return [p[0], p[1]]; });
            return;
          }
          if (calState.overrideActive) {
            calState.colorOverrides[ori][t][c] = corners.map(function(p) { return [p[0], p[1]]; });
          } else {
            calState.acabamentoCorners[ori][t] = corners.map(function(p) { return [p[0], p[1]]; });
          }
        }
        // No modo textura, o handle grava em data-tex-corners (warp da textura), não na arte.
        function applyCornersAttr() {
          var attr = (calState.tipo === 'textura') ? 'data-tex-corners' : 'data-corners';
          stage.setAttribute(attr, JSON.stringify(getCorners()));
        }
        function getOffset() {
          var o = calState.frameOffsets[ori][calState.tipo] && calState.frameOffsets[ori][calState.tipo][calState.cor];
          return o || { x: 0, y: 0 };
        }
        function setOffset(x, y) {
          if (!calState.frameOffsets[ori][calState.tipo]) calState.frameOffsets[ori][calState.tipo] = {};
          calState.frameOffsets[ori][calState.tipo][calState.cor] = {
            x: Math.round(x * 10) / 10,
            y: Math.round(y * 10) / 10
          };
          applyOffset();
          syncOffsetUI();
        }
        function applyOffset() {
          var off = getOffset();
          self._frames.forEach(function(f) {
            f.style.translate = (off.x || 0) + 'px ' + (off.y || 0) + 'px';
          });
        }

        function pushUndo() {
          var snap = JSON.stringify({ c: calState.acabamentoCorners, o: calState.colorOverrides });
          if (calState.undoStack.length && calState.undoStack[calState.undoStack.length - 1] === snap) return;
          calState.undoStack.push(snap);
          if (calState.undoStack.length > 30) calState.undoStack.shift();
        }

        // Toast
        var toastEl = document.createElement('div');
        toastEl.className = 'pp26-cal-toast';
        document.body.appendChild(toastEl);
        var toastTimer = null;
        function showToast(msg) {
          toastEl.textContent = msg;
          toastEl.classList.add('pp26-cal-toast--visible');
          clearTimeout(toastTimer);
          toastTimer = setTimeout(function() { toastEl.classList.remove('pp26-cal-toast--visible'); }, 2000);
        }

        // Stage ref
        var stage = this._perspectiveStages[0];
        if (!stage) return;

        // ---- TRAVA por quina, por acabamento ----
        // Duplo-clique numa quina trava/destrava. Travada não arrasta (você ajusta as
        // outras sem bagunçar essa). Estado por ori|acabamento.
        function lockArr() {
          var k = ori + '|' + calState.tipo;
          if (!calState.locked[k]) calState.locked[k] = [false, false, false, false];
          return calState.locked[k];
        }
        function applyLockVisual() {
          var L = lockArr();
          handles.forEach(function(h, i) {
            var on = !!L[i];
            h.classList.toggle('pp26-cal-handle--locked', on);
            h.style.background = on ? '#11d14b' : '';
            h.style.boxShadow = on ? '0 0 0 4px rgba(17,209,75,.4)' : '';
          });
        }

        // ---- HANDLES ----
        var handles = [];
        for (var hi = 0; hi < 4; hi++) {
          var h = document.createElement('div');
          h.className = 'pp26-cal-handle';
          h.dataset.index = hi;
          stage.appendChild(h);
          handles.push(h);
          attachDrag(h, hi);
          h.addEventListener('dblclick', function(e) {
            e.preventDefault(); e.stopPropagation();
            var i = +this.dataset.index, L = lockArr();
            L[i] = !L[i];
            applyLockVisual();
            showToast('Quina ' + (i + 1) + (L[i] ? ' travada 🔒' : ' destravada'));
          });
        }

        // Mesma caixa "contida" que o warp usa (object-fit:contain do frame quadrado
        // num stage mais alto → cw×ch deslocada ox,oy). Sem isso o handle cai fora
        // do canto real (off por oy ~26px). Espelha _applyWarpToStage.
        function frameBox() {
          var W = stage.offsetWidth, H = stage.offsetHeight, cw = W, ch = H, ox = 0, oy = 0;
          var fr = stage.querySelector('.pp26__frame:not([hidden])') || stage.querySelector('.pp26__frame');
          if (fr && fr.naturalWidth && fr.naturalHeight) {
            var fa = fr.naturalWidth / fr.naturalHeight;
            cw = Math.min(W, H * fa); ch = Math.min(H, W / fa);
            ox = (W - cw) / 2; oy = (H - ch) / 2;
          }
          return { W: W, H: H, ox: ox, oy: oy, cw: cw, ch: ch };
        }
        function positionHandles() {
          var corners = getCorners();
          if (!corners) return;
          var b = frameBox();
          corners.forEach(function(c, i) {
            handles[i].style.left = (b.ox + c[0] / 100 * b.cw) + 'px';
            handles[i].style.top = (b.oy + c[1] / 100 * b.ch) + 'px';
          });
          applyLockVisual();
        }

        function attachDrag(el, idx) {
          el.addEventListener('pointerdown', function(e) {
            if (lockArr()[idx]) { e.preventDefault(); e.stopPropagation(); showToast('Quina ' + (idx + 1) + ' travada 🔒 (2× clique destrava)'); return; }
            e.preventDefault();
            e.stopPropagation();
            try { el.setPointerCapture(e.pointerId); } catch (err) {}
            pushUndo();
            var rect = stage.getBoundingClientRect();
            var corners = getCorners().map(function(p) { return [p[0], p[1]]; });

            function onMove(ev) {
              var b = frameBox();
              var sx = rect.width / b.W, sy = rect.height / b.H;   // escala layout->tela (scale no mobile)
              var lx = (ev.clientX - rect.left) / sx, ly = (ev.clientY - rect.top) / sy;
              var x = (lx - b.ox) / b.cw * 100;
              var y = (ly - b.oy) / b.ch * 100;
              x = Math.max(-10, Math.min(110, x));
              y = Math.max(-10, Math.min(110, y));
              corners[idx] = [parseFloat(x.toFixed(2)), parseFloat(y.toFixed(2))];
              setCorners(corners);
              applyCornersAttr();
              self._applyAllWarps();
              positionHandles();
            }
            function onUp() {
              try { el.releasePointerCapture(e.pointerId); } catch (err) {}
              document.removeEventListener('pointermove', onMove);
              document.removeEventListener('pointerup', onUp);
            }
            document.addEventListener('pointermove', onMove);
            document.addEventListener('pointerup', onUp);
          });
        }

        function applyCorners() {
          applyCornersAttr();
          self._applyAllWarps();
          positionHandles();
        }

        // ---- PANEL ----
        var panel = document.createElement('div');
        panel.className = 'pp26-cal-panel';
        panel.innerHTML =
          '<h3>Calibração PP26</h3>' +
          '<div class="pp26-cal-panel__section">' +
            '<div class="pp26-cal-panel__label">Acabamento</div>' +
            '<div class="pp26-cal-btns" data-cal="tipo"></div>' +
          '</div>' +
          '<div class="pp26-cal-panel__section">' +
            '<div class="pp26-cal-panel__label">Cor</div>' +
            '<div class="pp26-cal-btns" data-cal="cor"></div>' +
          '</div>' +
          '<div class="pp26-cal-panel__section">' +
            '<div class="pp26-cal-override">' +
              '<input type="checkbox" id="pp26CalOverride">' +
              '<label for="pp26CalOverride">Ajuste fino desta cor</label>' +
            '</div>' +
          '</div>' +
          '<div class="pp26-cal-panel__section">' +
            '<div class="pp26-cal-panel__label">Offset da moldura</div>' +
            '<div class="pp26-cal-offset">' +
              '<label>X</label>' +
              '<input type="number" id="pp26CalOffX" value="0" step="0.5">' +
              '<label>Y</label>' +
              '<input type="number" id="pp26CalOffY" value="0" step="0.5">' +
            '</div>' +
            '<div class="pp26-cal-offset" style="margin-top:6px">' +
              '<div class="pp26-cal-offset__arrows">' +
                '<button data-dir="left">←</button>' +
                '<button data-dir="right">→</button>' +
                '<button data-dir="up">↑</button>' +
                '<button data-dir="down">↓</button>' +
                '<button data-dir="reset">•</button>' +
              '</div>' +
            '</div>' +
          '</div>' +
          '<div class="pp26-cal-panel__section" id="pp26CalSizeSec" style="display:none">' +
            '<div class="pp26-cal-panel__label">Tamanho (sem moldura)</div>' +
            '<div class="pp26-cal-offset">' +
              '<input type="range" id="pp26CalSize" min="0.3" max="2" step="0.01" value="1" style="flex:1">' +
              '<input type="number" id="pp26CalSizeNum" min="0.3" max="2" step="0.01" value="1" style="width:64px">' +
            '</div>' +
          '</div>' +
          '<div class="pp26-cal-panel__section">' +
            '<button class="pp26-cal-btn pp26-cal-btn--primary" data-cal="export">Copiar JSON</button>' +
          '</div>' +
          '<div class="pp26-cal-info">Orientação: <strong>' + ori + '</strong><br>Arraste os pontos. Cmd+Z pra desfazer.</div>';
        document.body.appendChild(panel);

        // Render tipo buttons
        var tipoWrap = panel.querySelector('[data-cal="tipo"]');
        TIPOS.forEach(function(t) {
          var btn = document.createElement('button');
          btn.className = 'pp26-cal-btn' + (t === calState.tipo ? ' pp26-cal-btn--active' : '');
          btn.textContent = TIPO_LABELS[t] || t;
          btn.dataset.tipo = t;
          btn.addEventListener('click', function() {
            calState.tipo = t;
            self._updateFrame(t, calState.cor);
            refreshPanel();
            setTimeout(function() { applyCorners(); applyOffset(); }, 400);
          });
          tipoWrap.appendChild(btn);
        });
        // Botão TEXTURA (warp separado do linho do canvas → grava em textureCorners)
        (function() {
          var tb = document.createElement('button');
          tb.className = 'pp26-cal-btn' + (calState.tipo === 'textura' ? ' pp26-cal-btn--active' : '');
          tb.textContent = 'Textura';
          tb.dataset.tipo = 'textura';
          tb.addEventListener('click', function() {
            calState.tipo = 'textura';
            self._updateFrame('canvas', calState.cor); // textura só existe no canvas
            refreshPanel();
            setTimeout(function() { applyCorners(); applyOffset(); }, 400);
          });
          tipoWrap.appendChild(tb);
        })();
        // Botão SEM MOLDURA (só tamanho/escala, não warp → grava em semMolduraScale)
        (function() {
          var sb = document.createElement('button');
          sb.className = 'pp26-cal-btn' + (calState.tipo === 'sem-moldura' ? ' pp26-cal-btn--active' : '');
          sb.textContent = 'Sem moldura';
          sb.dataset.tipo = 'sem-moldura';
          sb.addEventListener('click', function() {
            calState.tipo = 'sem-moldura';
            self._updateFrame('sem-moldura', 'default');
            refreshPanel();
            setTimeout(function() { applyCorners(); applyOffset(); }, 400);
          });
          tipoWrap.appendChild(sb);
        })();

        // Render cor buttons
        var corWrap = panel.querySelector('[data-cal="cor"]');
        CORES.forEach(function(c) {
          var btn = document.createElement('button');
          btn.className = 'pp26-cal-btn' + (c === calState.cor ? ' pp26-cal-btn--active' : '');
          btn.textContent = COR_LABELS[c] || (c.charAt(0).toUpperCase() + c.slice(1));
          btn.dataset.cor = c;
          btn.addEventListener('click', function() {
            calState.cor = c;
            self._updateFrame(calState.tipo, c);
            refreshPanel();
            setTimeout(function() { applyCorners(); applyOffset(); }, 400);
          });
          corWrap.appendChild(btn);
        });

        // Override checkbox
        var overrideCb = panel.querySelector('#pp26CalOverride');
        overrideCb.addEventListener('change', function() {
          calState.overrideActive = this.checked;
          if (this.checked && !calState.colorOverrides[ori][calState.tipo][calState.cor]) {
            // Copy current base corners as starting point
            calState.colorOverrides[ori][calState.tipo][calState.cor] =
              getCorners().map(function(p) { return [p[0], p[1]]; });
          }
          applyCorners();
        });

        // Offset inputs
        var offXInput = panel.querySelector('#pp26CalOffX');
        var offYInput = panel.querySelector('#pp26CalOffY');
        offXInput.addEventListener('input', function() {
          var off = getOffset();
          setOffset(parseFloat(this.value) || 0, off.y);
        });
        offYInput.addEventListener('input', function() {
          var off = getOffset();
          setOffset(off.x, parseFloat(this.value) || 0);
        });

        // Arrow buttons
        panel.querySelectorAll('.pp26-cal-offset__arrows button').forEach(function(btn) {
          btn.addEventListener('click', function() {
            var off = getOffset();
            var d = btn.dataset.dir;
            if (d === 'left') setOffset(off.x - 1, off.y);
            else if (d === 'right') setOffset(off.x + 1, off.y);
            else if (d === 'up') setOffset(off.x, off.y - 1);
            else if (d === 'down') setOffset(off.x, off.y + 1);
            else if (d === 'reset') setOffset(0, 0);
          });
        });

        // Tamanho (sem moldura) — escala em torno do centro, sem warp.
        var sizeSec = panel.querySelector('#pp26CalSizeSec');
        var sizeRange = panel.querySelector('#pp26CalSize');
        var sizeNum = panel.querySelector('#pp26CalSizeNum');
        function getScale() {
          var s = calState.semMolduraScale && calState.semMolduraScale[ori];
          return (typeof s === 'number' && s > 0) ? s : 1;
        }
        function setScale(v) {
          v = Math.max(0.3, Math.min(2, parseFloat(v) || 1));
          v = Math.round(v * 1000) / 1000;
          if (!calState.semMolduraScale) calState.semMolduraScale = {};
          calState.semMolduraScale[ori] = v;
          if (self._builderData) {
            self._builderData.semMolduraScale = self._builderData.semMolduraScale || {};
            self._builderData.semMolduraScale[ori] = v;
          }
          self._applyAllWarps();
          sizeRange.value = v; sizeNum.value = v;
        }
        sizeRange.addEventListener('input', function() { setScale(this.value); });
        sizeNum.addEventListener('input', function() { setScale(this.value); });

        // Export
        panel.querySelector('[data-cal="export"]').addEventListener('click', function() {
          var t = calState.tipo;
          // Escopa um mapa {ori:{tipo:...}} só pra orientação+acabamento atuais —
          // mesma forma do pp26_builder_json, então dá pra mesclar só esse bloco.
          function scope(map) {
            var o = {};
            if (map && map[ori] && map[ori][t] != null) { o[ori] = {}; o[ori][t] = map[ori][t]; }
            return o;
          }
          var exportData, label;
          if (t === 'textura') {
            var tex = {};
            if (calState.textureCorners && calState.textureCorners[ori] && calState.textureCorners[ori].canvas) {
              tex[ori] = { canvas: calState.textureCorners[ori].canvas };
            }
            exportData = {
              version: 1,
              generatedAt: new Date().toISOString(),
              orientation: ori,
              acabamento: 'textura',
              textureCorners: tex
            };
            label = 'Calibrate ' + ori + ' / Textura';
          } else if (t === 'sem-moldura') {
            var sm = {}; sm[ori] = getScale();
            exportData = {
              version: 1,
              generatedAt: new Date().toISOString(),
              orientation: ori,
              acabamento: 'sem-moldura',
              semMolduraScale: sm
            };
            label = 'Calibrate ' + ori + ' / Sem moldura';
          } else {
            exportData = {
              version: 1,
              generatedAt: new Date().toISOString(),
              orientation: ori,
              acabamento: t,
              acabamentoCorners: scope(calState.acabamentoCorners),
              colorOverrides: scope(calState.colorOverrides),
              frameOffsets: scope(calState.frameOffsets)
            };
            label = 'Calibrate ' + ori + ' / ' + (TIPO_LABELS[t] || t);
          }
          var json = JSON.stringify(exportData, null, 2);
          self._showCopyBox(json, label);
        });

        function syncOffsetUI() {
          var off = getOffset();
          offXInput.value = off.x || 0;
          offYInput.value = off.y || 0;
        }

        function refreshPanel() {
          // Update active buttons
          tipoWrap.querySelectorAll('.pp26-cal-btn').forEach(function(btn) {
            btn.classList.toggle('pp26-cal-btn--active', btn.dataset.tipo === calState.tipo);
          });
          corWrap.querySelectorAll('.pp26-cal-btn').forEach(function(btn) {
            btn.classList.toggle('pp26-cal-btn--active', btn.dataset.cor === calState.cor);
          });
          // Override checkbox
          var hasOverride = !!(calState.colorOverrides[ori][calState.tipo] && calState.colorOverrides[ori][calState.tipo][calState.cor]);
          overrideCb.checked = hasOverride;
          calState.overrideActive = hasOverride;
          // Modo sem-moldura: esconde as quinas (não é warp) e mostra o tamanho.
          var isSM = (calState.tipo === 'sem-moldura');
          handles.forEach(function(h) { h.style.display = isSM ? 'none' : ''; });
          if (sizeSec) sizeSec.style.display = isSM ? '' : 'none';
          if (isSM) { var _sc = getScale(); sizeRange.value = _sc; sizeNum.value = _sc; }
          syncOffsetUI();
        }

        // Keyboard: Cmd+Z undo, arrow keys for offset
        document.addEventListener('keydown', function(e) {
          if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
            e.preventDefault();
            if (!calState.undoStack.length) { showToast('Nada pra desfazer'); return; }
            var prev = JSON.parse(calState.undoStack.pop());
            calState.acabamentoCorners = prev.c;
            calState.colorOverrides = prev.o;
            applyCorners();
            showToast('Desfeito');
          }
          if (e.target.matches('input,textarea,select')) return;
          var off = getOffset();
          var step = e.shiftKey ? 0.5 : 1;
          if (e.key === 'ArrowLeft') { e.preventDefault(); setOffset(off.x - step, off.y); }
          else if (e.key === 'ArrowRight') { e.preventDefault(); setOffset(off.x + step, off.y); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setOffset(off.x, off.y - step); }
          else if (e.key === 'ArrowDown') { e.preventDefault(); setOffset(off.x, off.y + step); }
        });

        // Initial render
        positionHandles();
        syncOffsetUI();
        showToast('Modo calibração ativo');
      }

      // === TESTE LOCAL (interno) — editor SÓ da textura (canvas) ===
      _initTexEditor() {
        if (!new URLSearchParams(window.location.search).has('texedit')) return;
        var self = this, ori = this._orientation;
        var stage = this._perspectiveStages[0]; if (!stage) return;
        this.classList.add('pp26--calibrating'); // libera overflow do stage p/ mostrar as alças
        var curColor = (stage.querySelector('.pp26__frame') && stage.querySelector('.pp26__frame').dataset.frameColor) || 'mel';
        this._updateFrame('canvas', curColor);
        function artCorners() { try { return JSON.parse(stage.getAttribute('data-corners')); } catch (e) { return [[10,10],[90,10],[90,90],[10,90]]; } }
        var bd = this._builderData || {};
        var tc = (bd.textureCorners && bd.textureCorners[ori] && bd.textureCorners[ori].canvas)
          ? bd.textureCorners[ori].canvas.map(function(p){return [p[0],p[1]];}) : null;
        if (!tc) tc = artCorners();
        var opacity = (bd.texOpacity && bd.texOpacity[ori] != null) ? bd.texOpacity[ori] : 0.4;
        self.style.setProperty('--pp26-tex-op', opacity);
        var handles = [];
        function positionHandles(){ tc.forEach(function(c,i){ if(handles[i]){ handles[i].style.left=c[0]+'%'; handles[i].style.top=c[1]+'%'; } }); }
        function attachDrag(el, idx){
          el.addEventListener('pointerdown', function(e){ e.preventDefault(); e.stopPropagation();
            try { el.setPointerCapture(e.pointerId); } catch(_) {}
            var rect = stage.getBoundingClientRect();
            function mv(ev){ var x=(ev.clientX-rect.left)/rect.width*100, y=(ev.clientY-rect.top)/rect.height*100;
              x=Math.max(-10,Math.min(110,x)); y=Math.max(-10,Math.min(110,y));
              tc[idx]=[parseFloat(x.toFixed(2)),parseFloat(y.toFixed(2))];
              el.style.left=tc[idx][0]+'%'; el.style.top=tc[idx][1]+'%';
              stage.setAttribute('data-tex-corners', JSON.stringify(tc)); self._applyAllWarps(); }
            function up(){ document.removeEventListener('pointermove',mv); document.removeEventListener('pointerup',up); }
            document.addEventListener('pointermove',mv); document.addEventListener('pointerup',up);
          });
        }
        for (var i=0;i<4;i++){ var h=document.createElement('div'); h.className='pp26-cal-handle'; stage.appendChild(h); handles.push(h); attachDrag(h,i); }
        var panel = document.createElement('div'); panel.className='pp26-cal-panel';
        panel.innerHTML =
          '<h3>Textura — canvas (' + ori + ')</h3>' +
          '<div class="pp26-cal-panel__section"><div class="pp26-cal-panel__label">Opacidade <span id="ppTexOpV">'+Math.round(opacity*100)+'%</span></div>' +
          '<input type="range" id="ppTexOp" min="0" max="100" value="'+Math.round(opacity*100)+'" style="width:100%"></div>' +
          '<div class="pp26-cal-panel__section"><button class="pp26-cal-btn" id="ppTexToggle">👁 Esconder textura</button><button class="pp26-cal-btn" id="ppTexFrame" style="margin-top:6px">🚫 Esconder moldura</button></div>' +
          '<div class="pp26-cal-panel__section"><button class="pp26-cal-btn pp26-cal-btn--primary" id="ppTexExport">Copiar JSON</button></div>' +
          '<div class="pp26-cal-info">Arraste os pontos pra ajustar SÓ a textura (a arte não muda).</div>';
        document.body.appendChild(panel);
        panel.querySelector('#ppTexOp').addEventListener('input', function(){ opacity=this.value/100; self.style.setProperty('--pp26-tex-op',opacity); panel.querySelector('#ppTexOpV').textContent=this.value+'%'; });
        panel.querySelector('#ppTexToggle').addEventListener('click', function(){
          var tex = stage.querySelector('.pp26__art-tex'); if (!tex) return;
          var hidden = tex.style.display === 'none';
          tex.style.display = hidden ? '' : 'none';
          this.textContent = hidden ? '👁 Esconder textura' : '🖼 Mostrar textura';
        });
        panel.querySelector('#ppTexFrame').addEventListener('click', function(){
          var fr = stage.querySelector('.pp26__frame'); if (!fr) return;
          var hidden = fr.style.display === 'none';
          fr.style.display = hidden ? '' : 'none';
          this.textContent = hidden ? '🚫 Esconder moldura' : '🖼 Mostrar moldura';
        });
        panel.querySelector('#ppTexExport').addEventListener('click', function(){
          var out={ textureCorners:{}, texOpacity:{} }; out.textureCorners[ori]={canvas:tc}; out.texOpacity[ori]=opacity;
          var json=JSON.stringify(out,null,2);
          self._showCopyBox(json, 'Textura — ' + ori);
        });
        stage.setAttribute('data-tex-corners', JSON.stringify(tc)); self._applyAllWarps(); positionHandles();
        setTimeout(function(){ self._applyAllWarps(); positionHandles(); }, 500);
      }
      // === FIM TESTE LOCAL texedit ===

      // Aplica multiplicadores de tamanho salvos (por orientação) — persiste no load normal.
      _applySizeMul() {
        var t = (this._currentType) || (this._frames[0] && this._frames[0].dataset.frameType) || null;
        this._applySizeMulFor(t);
      }
      // Aplica tamanho/posição-X. Sem-moldura pode ter override próprio (smMob/smMobX)
      // por orientação — quando selecionado, usa esses valores no lugar dos da orientação.
      _applySizeMulFor(type) {
        var sm = this._builderData && this._builderData.sizeMul && this._builderData.sizeMul[this._orientation];
        if (!sm) return;
        var isSM = (type === 'sem-moldura');
        var mob  = (isSM && sm.smMob  != null) ? sm.smMob  : sm.mob;
        var mobX = (isSM && sm.smMobX != null) ? sm.smMobX : sm.mobX;
        if (mob != null) this.style.setProperty('--pp26-sz-mob', mob);
        if (sm.tab != null) this.style.setProperty('--pp26-sz-tab', sm.tab);
        if (sm.desk != null) this.style.setProperty('--pp26-sz-desk', sm.desk);
        if (mobX != null) this.style.setProperty('--pp26-szx-mob', mobX + 'px');
      }

      // === TESTE LOCAL (interno) — editor de tamanho por device ?sizeedit ===
      _initSizeEditor() {
        if (!new URLSearchParams(window.location.search).has('sizeedit')) return;
        var self = this, ori = this._orientation;
        var bd = this._builderData || {}; var sm = (bd.sizeMul && bd.sizeMul[ori]) || {};
        var cur = { mob: sm.mob != null ? sm.mob : 1, tab: sm.tab != null ? sm.tab : 1, desk: sm.desk != null ? sm.desk : 1, mobX: sm.mobX != null ? sm.mobX : 0,
                    smMob: sm.smMob != null ? sm.smMob : (sm.mob != null ? sm.mob : 1), smMobX: sm.smMobX != null ? sm.smMobX : (sm.mobX != null ? sm.mobX : 0) };
        function isSM(){ return ((self._currentType) || (self._frames[0] && self._frames[0].dataset.frameType)) === 'sem-moldura'; }
        this.style.setProperty('--pp26-sz-mob', cur.mob);
        this.style.setProperty('--pp26-sz-tab', cur.tab);
        this.style.setProperty('--pp26-sz-desk', cur.desk);
        this.style.setProperty('--pp26-szx-mob', cur.mobX + 'px');
        function bp(){ var w=window.innerWidth; return w<=959?'mob':(w<=1067?'tab':'desk'); }
        function reWarp(){ if(self._syncStageAspectToFrame) self._syncStageAspectToFrame(); requestAnimationFrame(function(){ self._applyAllWarps(); }); }
        var panel=document.createElement('div'); panel.className='pp26-cal-panel';
        panel.innerHTML =
          '<h3>Tamanho — '+ori+' <button id="ppSzHide" style="float:right;border:0;background:#eee;border-radius:4px;width:22px;height:22px;cursor:pointer;font-size:13px;line-height:1">✕</button></h3>'+
          '<div class="pp26-cal-info">Breakpoint atual: <strong id="ppSzBp"></strong><br>Redimensione a janela p/ editar mobile/tablet/desktop.</div>'+
          '<div class="pp26-cal-panel__section"><div class="pp26-cal-panel__label">Tamanho <span id="ppSzV"></span></div>'+
          '<input type="range" id="ppSz" min="50" max="170" step="1" style="width:100%"></div>'+
          '<div class="pp26-cal-panel__section"><div class="pp26-cal-panel__label">Posição X mobile <span id="ppSzXV"></span></div>'+
          '<input type="range" id="ppSzX" min="-150" max="150" step="1" style="width:100%"></div>'+
          '<div class="pp26-cal-info" id="ppSzAll"></div>'+
          '<div class="pp26-cal-info" id="ppSzPx" style="font-weight:600"></div>'+
          '<div class="pp26-cal-panel__section"><button class="pp26-cal-btn pp26-cal-btn--primary" id="ppSzExport">Copiar JSON</button></div>';
        document.body.appendChild(panel);
        // botão flutuante pra reabrir o painel depois de ocultar
        var reopen=document.createElement('button');
        reopen.textContent='⊕ tamanho';
        reopen.style.cssText='position:fixed;bottom:12px;right:12px;z-index:99999;display:none;background:#111;color:#fff;border:0;padding:8px 11px;border-radius:6px;font:600 12px sans-serif;cursor:pointer;box-shadow:0 2px 8px rgba(0,0,0,.4)';
        document.body.appendChild(reopen);
        panel.querySelector('#ppSzHide').addEventListener('click', function(){ panel.style.display='none'; reopen.style.display='block'; });
        reopen.addEventListener('click', function(){ panel.style.display=''; reopen.style.display='none'; });
        var slider=panel.querySelector('#ppSz');
        var sliderX=panel.querySelector('#ppSzX');
        sliderX.addEventListener('input', function(){ var v=parseInt(this.value,10); if(isSM()) cur.smMobX=v; else cur.mobX=v; self.style.setProperty('--pp26-szx-mob', v+'px'); panel.querySelector('#ppSzXV').textContent=v+'px'; });
        function allTxt(){ return 'mob '+Math.round(cur.mob*100)+'% · tab '+Math.round(cur.tab*100)+'% · desk '+Math.round(cur.desk*100)+'% | SM '+Math.round(cur.smMob*100)+'% X'+cur.smMobX; }
        function stagePx(){ var st=self._perspectiveStages[0]; if(!st) return '—'; var r=st.getBoundingClientRect(); return Math.round(r.width)+' × '+Math.round(r.height)+'px'; }
        function updatePx(){ var el=panel.querySelector('#ppSzPx'); if(el) el.innerHTML='Viewport: '+window.innerWidth+' × '+window.innerHeight+'px<br>Stage: '+stagePx(); }
        function updatePxSettled(){ updatePx(); [120,300,540].forEach(function(t){ setTimeout(updatePx, t); }); }
        function refresh(){ var b=bp(); var smOn=isSM();
          var sz=(b==='mob'&&smOn)?cur.smMob:cur[b];
          slider.value=Math.round(sz*100); panel.querySelector('#ppSzV').textContent=Math.round(sz*100)+'%';
          var xv=smOn?cur.smMobX:cur.mobX; sliderX.value=xv; panel.querySelector('#ppSzXV').textContent=xv+'px';
          self.style.setProperty('--pp26-sz-mob', smOn?cur.smMob:cur.mob);
          self.style.setProperty('--pp26-szx-mob', (smOn?cur.smMobX:cur.mobX)+'px');
          panel.querySelector('#ppSzBp').textContent=b+(smOn?' · SEM MOLDURA':''); panel.querySelector('#ppSzAll').textContent=allTxt(); updatePxSettled(); }
        slider.addEventListener('input', function(){ var b=bp(); var v=this.value/100; if(b==='mob'&&isSM()){cur.smMob=v;} else {cur[b]=v;} self.style.setProperty('--pp26-sz-'+b, v); panel.querySelector('#ppSzV').textContent=this.value+'%'; panel.querySelector('#ppSzAll').textContent=allTxt(); reWarp(); updatePxSettled(); });
        window.addEventListener('resize', refresh, { passive:true });
        self.addEventListener('pp26:frame-updated', function(){ setTimeout(refresh, 60); }); // troca acabamento → atualiza alvo (sem-moldura tem valores próprios)
        panel.querySelector('#ppSzExport').addEventListener('click', function(){
          var out={ sizeMul:{} }; out.sizeMul[ori]={ mob:cur.mob, tab:cur.tab, desk:cur.desk, mobX:cur.mobX, smMob:cur.smMob, smMobX:cur.smMobX };
          var json=JSON.stringify(out,null,2);
          self._showCopyBox(json, 'Tamanho — ' + ori);
        });
        refresh();
      }

      // === TESTE LOCAL (interno) — badge de resolução ?res ===
      // Mostra viewport (W×H) + breakpoint fixo no canto, pra documentar em que
      // resolução a arte vaza ao dar print. Independente do painel de tamanho.
      _initResBadge() {
        if (!new URLSearchParams(window.location.search).has('res')) return;
        var b = document.createElement('div');
        b.style.cssText = 'position:fixed;top:8px;left:8px;z-index:99999;background:#111;color:#0f0;'
          + 'font:600 12px/1.3 ui-monospace,Menlo,monospace;padding:6px 9px;border-radius:6px;'
          + 'pointer-events:none;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,.4)';
        document.body.appendChild(b);
        function bpName(){ var w=window.innerWidth; return w<=959?'mob':(w<=1067?'tab':'desk'); }
        function upd(){ b.textContent = window.innerWidth + ' × ' + window.innerHeight + '  [' + bpName() + ']'; }
        upd();
        window.addEventListener('resize', upd, { passive: true });
      }

      // === TESTE LOCAL (interno) — painel ?fxedit ===
      // Tuner ao vivo da transição 3D "depth push/pull" entre acabamentos.
      // Seta as vars --pp26fx-* no :root (herdadas pelas pseudo ::view-transition).
      _initFxEditor() {
        if (!new URLSearchParams(window.location.search).has('fxedit')) return;
        var self = this;
        var EASES = {
          'suave (out-expo)': 'cubic-bezier(0.22, 1, 0.36, 1)',
          'suave (out-quint)': 'cubic-bezier(0.23, 1, 0.32, 1)',
          'padrão (out-cubic)': 'cubic-bezier(0.33, 1, 0.68, 1)',
          'in-out (suave)': 'cubic-bezier(0.65, 0, 0.35, 1)',
          'spring-ish (back)': 'cubic-bezier(0.34, 1.56, 0.64, 1)',
          'linear': 'linear'
        };
        var EFFECTS = { fade: 'Fade (soft)', depth: 'Depth push/pull', tilt: 'Tilt 3D', flip: 'Card flip', slide: 'Slide', zoom: 'Zoom', blur: 'Blur dissolve' };
        var FIELDS = [
          { k: 'dur', label: 'Duração (ms)', min: 120, max: 1200, step: 10 },
          { k: 'depth', label: 'Profundidade Z (px)', min: 0, max: 360, step: 5 },
          { k: 'angle', label: 'Ângulo (°)', min: 0, max: 180, step: 1 },
          { k: 'slide', label: 'Slide (px)', min: 0, max: 240, step: 5 },
          { k: 'scaleOut', label: 'Escala saída', min: 0.6, max: 1, step: 0.01 },
          { k: 'scaleIn', label: 'Escala entrada', min: 1, max: 1.4, step: 0.01 },
          { k: 'blur', label: 'Blur (px)', min: 0, max: 20, step: 1 },
          { k: 'persp', label: 'Perspectiva (px)', min: 500, max: 2600, step: 50 }
        ];
        function effOpts(sel) { return Object.keys(EFFECTS).map(function (k) { return '<option value="' + k + '"' + (k === sel ? ' selected' : '') + '>' + EFFECTS[k] + '</option>'; }).join(''); }
        function easeOpts(sel) { return Object.keys(EASES).map(function (k) { return '<option value="' + EASES[k] + '"' + (EASES[k] === sel ? ' selected' : '') + '>' + k + '</option>'; }).join(''); }
        function sectionHTML(key, title) {
          var c = self._fx[key];
          var rows = FIELDS.map(function (f) {
            return '<div class="pp26-cal-panel__section"><div class="pp26-cal-panel__label">' + f.label + ' <span id="' + key + '_' + f.k + 'V">' + c[f.k] + '</span></div>' +
              '<input type="range" data-sec="' + key + '" data-k="' + f.k + '" min="' + f.min + '" max="' + f.max + '" step="' + f.step + '" value="' + c[f.k] + '" style="width:100%"></div>';
          }).join('');
          return '<div style="border-top:1px solid #444;margin-top:10px;padding-top:8px"><strong style="font-size:12px;letter-spacing:.04em">' + title + '</strong>' +
            '<div class="pp26-cal-panel__section"><div class="pp26-cal-panel__label">Efeito</div><select data-sec="' + key + '" data-k="effect" style="width:100%">' + effOpts(c.effect) + '</select></div>' +
            '<div class="pp26-cal-panel__section"><div class="pp26-cal-panel__label">Easing</div><select data-sec="' + key + '" data-k="ease" style="width:100%">' + easeOpts(c.ease) + '</select></div>' +
            rows +
            '<div class="pp26-cal-panel__section"><button class="pp26-cal-btn" data-test="' + key + '">▶ Testar ' + (key === 'frame' ? 'moldura' : 'cor') + '</button></div></div>';
        }
        var panel = document.createElement('div');
        panel.className = 'pp26-cal-panel';
        panel.style.maxHeight = '92vh';
        panel.style.overflowY = 'auto';
        panel.innerHTML = '<h3>Efeitos de transição</h3>' +
          '<div class="pp26-cal-info" id="fxStatus"></div>' +
          sectionHTML('frame', 'ENTRE MOLDURAS') +
          sectionHTML('color', 'ENTRE CORES (soft)') +
          '<div class="pp26-cal-panel__section"><button class="pp26-cal-btn pp26-cal-btn--primary" id="fxExport">Copiar JSON</button></div>';
        document.body.appendChild(panel);
        panel.querySelectorAll('input[data-sec], select[data-sec]').forEach(function (el) {
          var sec = el.getAttribute('data-sec'), k = el.getAttribute('data-k');
          var ev = el.tagName === 'SELECT' ? 'change' : 'input';
          el.addEventListener(ev, function () {
            self._fx[sec][k] = (el.tagName === 'SELECT') ? this.value : parseFloat(this.value);
            var span = panel.querySelector('#' + sec + '_' + k + 'V'); if (span) span.textContent = this.value;
            self._applyFx(self._fx[sec]);
          });
        });
        var COLS = ['mel', 'preta', 'pinus', 'branca'];
        var TYPES = ['canvas', 'padrao', 'caixa', 'sem-moldura'];
        panel.querySelectorAll('button[data-test]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var key = btn.getAttribute('data-test');
            var fr = self._frames[0];
            var curT = (fr && fr.dataset.frameType) || 'canvas';
            var curC = (fr && fr.dataset.frameColor) || 'mel';
            self._applyFx(self._fx[key]);
            if (key === 'frame') { self._updateFrame(TYPES[(TYPES.indexOf(curT) + 1) % TYPES.length], curC); }
            else { self._updateFrame(curT, COLS[(COLS.indexOf(curC) + 1) % COLS.length]); }
          });
        });
        panel.querySelector('#fxExport').addEventListener('click', function () {
          var j = JSON.stringify({ fx: { frame: self._fx.frame, color: self._fx.color } }, null, 2);
          if (self._showCopyBox) self._showCopyBox(j, 'Efeitos de transição');
          else if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(j).then(function () { panel.querySelector('#fxStatus').textContent = 'Copiado!'; }, function () { prompt('Copie:', j); });
          else prompt('Copie:', j);
        });
        panel.querySelector('#fxStatus').textContent = 'Motor overlay (página não congela)';
      }

      // === TESTE LOCAL (interno) — editor ?posedit ===
      // Alinha a POSIÇÃO do grupo (moldura+arte+textura) por tipo×cor pra não
      // "sambar". Arrasta no quadro, vê ghosts dos centros dos outros tipos,
      // aplica em todas as cores ou só na atual, exporta framePos.
      _initPosEditor() {
        if (!new URLSearchParams(window.location.search).has('posedit')) return;
        var self = this, ori = this._orientation;
        var stage = this._perspectiveStages[0];
        if (!stage) return;
        var TYPES = ['canvas', 'padrao', 'padrao-com-paspatur', 'caixa'];
        var COLORS = ['mel', 'preta', 'pinus', 'branca'];
        var fr0 = self._frames[0];
        var stx = { type: (fr0 && fr0.dataset.frameType) || 'canvas', color: (fr0 && fr0.dataset.frameColor) || 'mel', all: true, showAll: false };
        function frameUrl(t) { return self._resolveFrameUrl(ori, t, stx.color, (fr0 && fr0.src) || ''); }

        function ensure(t) { self._framePos[ori] = self._framePos[ori] || {}; self._framePos[ori][t] = self._framePos[ori][t] || {}; return self._framePos[ori][t]; }
        function getOff() { var bt = (self._framePos[ori] && self._framePos[ori][stx.type]) || {}; var p = bt[stx.color] || bt['*'] || {}; return { x: p.x || 0, y: p.y || 0, s: (p.s != null ? p.s : 1) }; }
        function writeOff(t, o) {
          var bt = ensure(t); var v = { x: Math.round(o.x * 100) / 100, y: Math.round(o.y * 100) / 100, s: Math.round(o.s * 1000) / 1000 };
          if (stx.all) { bt['*'] = v; COLORS.forEach(function (c) { delete bt[c]; }); }
          else { bt[stx.color] = v; }
        }
        function setOff(x, y, s) {
          var cur = getOff();
          writeOff(stx.type, { x: x, y: y, s: (s != null ? s : cur.s) });
          self._applyFramePos(stx.type, stx.color); drawGhosts(); updateUI();
        }
        function centroid(t) {
          var c = self._builderData && self._builderData.acabamentoCorners && self._builderData.acabamentoCorners[ori] && self._builderData.acabamentoCorners[ori][t];
          if (!c) return null; var sx = 0, sy = 0; c.forEach(function (p) { sx += p[0]; sy += p[1]; }); return { x: sx / 4, y: sy / 4 };
        }
        // grava offset pra um tipo arbitrário (respeita "todas as cores"); preserva s
        function setOffFor(t, x, y) {
          var bt = ensure(t); var prev = bt[stx.color] || bt['*'] || {};
          writeOff(t, { x: x, y: y, s: (prev.s != null ? prev.s : 1) });
        }
        // CENTRALIZAR: encaixa o centro da abertura do tipo no centro do stage (50,50).
        function centralizar(t) { var ce = centroid(t); if (!ce) return; setOffFor(t, 50 - ce.x, 50 - ce.y); }

        // overlay de guia/ghosts FORA do stage (irmão), sobre a caixa de layout
        // do stage (não translada junto com o grupo).
        var host = stage.offsetParent || stage.parentNode;
        if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
        var overlay = document.createElement('div');
        overlay.style.cssText = 'position:absolute;pointer-events:none;z-index:50;overflow:visible;';
        host.appendChild(overlay);
        function placeOverlay() { overlay.style.left = stage.offsetLeft + 'px'; overlay.style.top = stage.offsetTop + 'px'; overlay.style.width = stage.offsetWidth + 'px'; overlay.style.height = stage.offsetHeight + 'px'; }
        function drawGhosts() {
          placeOverlay();
          overlay.innerHTML =
            '<div style="position:absolute;left:50%;top:0;width:1px;height:100%;background:rgba(255,107,53,.45)"></div>' +
            '<div style="position:absolute;top:50%;left:0;height:1px;width:100%;background:rgba(255,107,53,.45)"></div>';
          // VER TODAS as molduras (1 cor) sobrepostas — cada tipo no SEU offset,
          // pra enxergar tamanho/abertura e alinhar.
          if (stx.showAll) {
            TYPES.forEach(function (t) {
              var bt = (self._framePos[ori] && self._framePos[ori][t]) || {};
              var off = bt[stx.color] || bt['*'] || {};
              var cur = (t === stx.type);
              var im = document.createElement('img');
              im.src = frameUrl(t);
              im.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;' +
                'opacity:' + (cur ? 0.85 : 0.4) + ';transform:translate(' + (off.x || 0) + '%,' + (off.y || 0) + '%) scale(' + (off.s != null ? off.s : 1) + ');' +
                (cur ? 'outline:2px solid #ff6b35;outline-offset:-2px;' : '');
              overlay.appendChild(im);
            });
          }
          // GHOST madeira-escura (gabarito): a moldura 'mel' do tipo atual no offset
          // dela, faint, pra encaixar as outras cores nela.
          if (stx.ghostME && stx.color !== 'mel') {
            var btm = (self._framePos[ori] && self._framePos[ori][stx.type]) || {};
            var offm = btm['mel'] || btm['*'] || {};
            var gm = document.createElement('img');
            gm.src = self._resolveFrameUrl(ori, stx.type, 'mel', (fr0 && fr0.src) || '');
            gm.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;' +
              'opacity:0.5;filter:hue-rotate(120deg) saturate(2);' +
              'transform:translate(' + (offm.x || 0) + '%,' + (offm.y || 0) + '%) scale(' + (offm.s != null ? offm.s : 1) + ');';
            overlay.appendChild(gm);
          }
          TYPES.forEach(function (t) {
            var ce = centroid(t); if (!ce) return;
            var bt = (self._framePos[ori] && self._framePos[ori][t]) || {};
            var off = bt[stx.color] || bt['*'] || { x: 0, y: 0 };
            var x = ce.x + (off.x || 0), y = ce.y + (off.y || 0);
            var cur = (t === stx.type);
            var dot = document.createElement('div');
            dot.title = t;
            dot.style.cssText = 'position:absolute;transform:translate(-50%,-50%);left:' + x + '%;top:' + y + '%;width:' + (cur ? 18 : 11) + 'px;height:' + (cur ? 18 : 11) + 'px;border-radius:50%;background:' + (cur ? '#ff6b35' : 'rgba(0,110,255,.55)') + ';border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);';
            overlay.appendChild(dot);
          });
        }

        // drag no stage
        var drag = false, sX, sY, sOff;
        stage.style.cursor = 'move';
        stage.addEventListener('pointerdown', function (e) { drag = true; sX = e.clientX; sY = e.clientY; sOff = getOff(); try { stage.setPointerCapture(e.pointerId); } catch (_) {} e.preventDefault(); });
        stage.addEventListener('pointermove', function (e) { if (!drag) return; var r = stage.getBoundingClientRect(); setOff(sOff.x + (e.clientX - sX) / r.width * 100, sOff.y + (e.clientY - sY) / r.height * 100); });
        stage.addEventListener('pointerup', function () { drag = false; });
        window.addEventListener('resize', function () { setTimeout(drawGhosts, 60); }, { passive: true });

        // painel
        var panel = document.createElement('div'); panel.className = 'pp26-cal-panel';
        panel.innerHTML =
          '<h3 style="display:flex;justify-content:space-between;align-items:center">Posição / Alinhar <button id="poMin" style="background:#333;color:#eee;border:1px solid #555;border-radius:5px;cursor:pointer;padding:2px 8px;font-size:14px;line-height:1">–</button></h3>' +
          '<div id="poBody">' +
          '<div class="pp26-cal-info" id="poInfo"></div>' +
          '<div class="pp26-cal-panel__section"><label style="display:flex;gap:8px;align-items:center;cursor:pointer"><input type="checkbox" id="poShowAll"> ver TODAS as molduras (1 cor)</label></div>' +
          '<div class="pp26-cal-panel__section"><label style="display:flex;gap:8px;align-items:center;cursor:pointer"><input type="checkbox" id="poGhostME"> ghost madeira-escura (gabarito)</label></div>' +
          '<div class="pp26-cal-panel__section"><div class="pp26-cal-panel__label">Tipo</div><div class="sw" style="display:flex;gap:6px"><button class="pp26-cal-btn" id="poTypePrev" style="flex:1">◀</button><button class="pp26-cal-btn" id="poTypeNext" style="flex:1">▶</button></div></div>' +
          '<div class="pp26-cal-panel__section"><div class="pp26-cal-panel__label">Cor</div><div class="sw" style="display:flex;gap:6px"><button class="pp26-cal-btn" id="poColPrev" style="flex:1">◀</button><button class="pp26-cal-btn" id="poColNext" style="flex:1">▶</button></div></div>' +
          '<div class="pp26-cal-panel__section"><label style="display:flex;gap:8px;align-items:center;cursor:pointer"><input type="checkbox" id="poAll" checked> aplicar em TODAS as cores</label></div>' +
          '<div class="pp26-cal-panel__section"><div class="pp26-cal-panel__label">Nudge</div><div style="display:flex;gap:4px;flex-wrap:wrap"><button class="pp26-cal-btn" data-nud="l">←</button><button class="pp26-cal-btn" data-nud="r">→</button><button class="pp26-cal-btn" data-nud="u">↑</button><button class="pp26-cal-btn" data-nud="d">↓</button><button class="pp26-cal-btn" data-nud="0">reset xy</button></div></div>' +
          '<div class="pp26-cal-panel__section"><div class="pp26-cal-panel__label">Escala (tamanho)</div><div style="display:flex;gap:4px"><button class="pp26-cal-btn" data-nud="sm">−</button><button class="pp26-cal-btn" data-nud="sp">+</button><button class="pp26-cal-btn" data-nud="s1">1.0</button></div></div>' +
          '<div class="pp26-cal-panel__section"><div class="pp26-cal-panel__label">Centralizar (abertura no eixo)</div><div style="display:flex;gap:6px"><button class="pp26-cal-btn" id="poCenter" style="flex:1">este tipo</button><button class="pp26-cal-btn" id="poCenterAll" style="flex:1">todos</button></div></div>' +
          '<div class="pp26-cal-panel__section"><button class="pp26-cal-btn pp26-cal-btn--primary" id="poExport">Copiar JSON</button></div>' +
          '<div class="pp26-cal-info">Cruz = centro do stage. Clique "centralizar" pra encaixar a abertura no eixo, "ver todas" pra conferir, e arraste/nudge pra ajuste fino.</div>' +
          '</div>';
        document.body.appendChild(panel);
        panel.querySelector('#poMin').onclick = function () {
          var b = panel.querySelector('#poBody');
          var hid = b.style.display === 'none';
          b.style.display = hid ? '' : 'none';
          this.textContent = hid ? '–' : '+';
        };
        panel.querySelector('#poShowAll').onchange = function () { stx.showAll = this.checked; drawGhosts(); };
        panel.querySelector('#poCenter').onclick = function () { centralizar(stx.type); self._applyFramePos(stx.type, stx.color); drawGhosts(); updateUI(); };
        panel.querySelector('#poCenterAll').onclick = function () { TYPES.forEach(centralizar); self._applyFramePos(stx.type, stx.color); drawGhosts(); updateUI(); };
        function updateUI() {
          var o = getOff();
          panel.querySelector('#poInfo').innerHTML = '<strong>' + stx.type + '</strong> · ' + (stx.all ? 'todas as cores' : stx.color) + '<br>offset: ' + o.x + '% , ' + o.y + '% · escala: ' + o.s;
        }
        function syncFrame() { self._updateFrame(stx.type, stx.color); }
        panel.querySelector('#poTypePrev').onclick = function () { stx.type = TYPES[(TYPES.indexOf(stx.type) - 1 + TYPES.length) % TYPES.length]; syncFrame(); drawGhosts(); updateUI(); };
        panel.querySelector('#poTypeNext').onclick = function () { stx.type = TYPES[(TYPES.indexOf(stx.type) + 1) % TYPES.length]; syncFrame(); drawGhosts(); updateUI(); };
        panel.querySelector('#poColPrev').onclick = function () { stx.color = COLORS[(COLORS.indexOf(stx.color) - 1 + COLORS.length) % COLORS.length]; syncFrame(); drawGhosts(); updateUI(); };
        panel.querySelector('#poColNext').onclick = function () { stx.color = COLORS[(COLORS.indexOf(stx.color) + 1) % COLORS.length]; syncFrame(); drawGhosts(); updateUI(); };
        panel.querySelector('#poAll').onchange = function () { stx.all = this.checked; updateUI(); };
        panel.querySelectorAll('button[data-nud]').forEach(function (b) {
          b.onclick = function () {
            var o = getOff(), k = 0.3, sk = 0.01; var d = b.getAttribute('data-nud');
            if (d === 'l') setOff(o.x - k, o.y);
            else if (d === 'r') setOff(o.x + k, o.y);
            else if (d === 'u') setOff(o.x, o.y - k);
            else if (d === 'd') setOff(o.x, o.y + k);
            else if (d === '0') setOff(0, 0, o.s);
            else if (d === 'sm') setOff(o.x, o.y, o.s - sk);
            else if (d === 'sp') setOff(o.x, o.y, o.s + sk);
            else if (d === 's1') setOff(o.x, o.y, 1);
          };
        });
        panel.querySelector('#poGhostME').onchange = function () { stx.ghostME = this.checked; drawGhosts(); };
        panel.querySelector('#poExport').onclick = function () {
          var j = JSON.stringify({ framePos: self._framePos }, null, 2);
          if (self._showCopyBox) self._showCopyBox(j, 'Posição (framePos)');
          else if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(j).then(function () { panel.querySelector('#poInfo').textContent = 'Copiado!'; }, function () { prompt('Copie:', j); });
          else prompt('Copie:', j);
        };
        // adiciona classe pra stage mostrar overflow (ver o quadro arrastar) — opcional
        drawGhosts(); updateUI();
      }
      // === FIM TESTE LOCAL sizeedit ===
    });
  }
})();
